import React, { useState, useEffect, useMemo } from 'react';
import {
  FileText,
  Clock,
  MessageSquare,
  AlertTriangle,
  MoreVertical,
  ArrowRight,
  Plus,
  ShieldAlert,
  CheckCircle2,
  Settings,
  Layers,
  ChevronRight,
  ExternalLink,
  Info,
  SlidersHorizontal,
  Save,
  RotateCcw,
  X,
  Trash2,
  ArrowLeft,
  ArrowUp,
  ArrowDown,
  Shield,
  Activity,
  CheckSquare,
  ClipboardCheck,
  Building,
  Plane,
  Grid,
  Network,
  ShieldCheck,
  GitBranch,
  Search,
  Filter
} from 'lucide-react';
import { dashboardService } from './dashboardService';
import { useEscapeKey } from '../../shared/useGlobalModalEscape';
import { getActiveSessionOrganizationCode, getActiveSessionUserId } from '../../shared/api';
import { isModuleAllowedForRole } from '../../shared/rolePermissions';
import { TablePagination, useTablePagination } from '../../shared/TablePagination';
import { ReportDetailsModal } from '../../components/ReportDetailsModal';
import { MissingIndicatorAlertItem, ActiveThresholdAlertItem } from '../safety/performance/types';
import {
  WIDGET_CATALOG,
  ConfiguredWidgetItem,
  WidgetSize,
  WidgetCatalogItem,
  getAllowedWidgetsForRole,
  getUserDashboardLayout,
  saveUserDashboardLayout,
  resetUserDashboardLayout,
  getRoleDefaultLayout
} from './dashboardLayoutConfig';
import {
  IndicatorRecordItem,
  IndicatorModalState,
  OVERDUE_REPORTS_DATA,
  IN_PROGRESS_REPORTS_DATA,
  PENDING_REPORTS_DATA,
  WAITING_ASSESS_HIGH_DATA,
  WAITING_ASSESS_MED_DATA,
  WAITING_ASSESS_LOW_DATA,
  FEED_UPDATES_UNREAD_DATA,
  FEED_UPDATES_READ_DATA,
  OVERDUE_ACTIONS_SAFETY_DATA,
  OVERDUE_ACTIONS_INVS_DATA,
  CRITICAL_RISKS_RED_DATA,
  CRITICAL_RISKS_AMBER_DATA,
  OPEN_INVS_INQUIRY_DATA,
  OPEN_INVS_RCA_DATA,
  OPEN_INVS_FINAL_DATA,
  QUALITY_FINDINGS_L1_DATA,
  QUALITY_FINDINGS_L2_DATA,
  COMPLIANCE_EASA_DATA,
  COMPLIANCE_ICAO_DATA,
  MOC_IMPACT_DATA,
  MOC_APPROVAL_DATA,
  TELEMETRY_STREAM_DATA,
  TELEMETRY_DLQ_DATA
} from './dashboardIndicatorData';

interface DashboardPageProps {
  onNavigate?: (tab: string, extraParams?: Record<string, string | null>) => void;
  userName?: string;
  userRole?: string;
  userId?: string;
}

interface MyReportRow {
  id: string;
  title: string;
  status: string;
  statusColor: 'blue' | 'amber' | 'cyan';
  dueDate: string;
  daysLeft: number;
  isUrgent: boolean;
  targetTab: string;
}

interface WaitingReportRow {
  id: string;
  title: string;
  kindOfReport: string;
  severity: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
  received: string;
  targetTab: string;
}

interface InvestigationRow {
  id: string;
  title: string;
  leadInvestigator: string;
  phase: string;
  targetDate: string;
  status: string;
}

interface AuditRow {
  id: string;
  title: string;
  type: string;
  auditor: string;
  scheduledDate: string;
  status: string;
}

export interface ReportActivityLogItem {
  id: string;
  actorName: string;
  actorDepartment: string;
  actorInitials: string;
  avatarBg: string;
  actionText: string;
  tag: string;
  timestamp: string;
  targetTab: string;
  targetId: string;
  involvesUser: boolean;
  assignedToUserId?: string;
  updateType?: 'ATTACHMENT' | 'COMMENT' | 'VERIFICATION' | 'STATUS_CHANGE' | 'RCA';
}

export interface SystemActivityLogItem {
  id: string;
  domainTitle: string;
  domainModule: string; // The module id for role permission verification (RULE: isModuleAllowedForRole)
  iconColor: string;
  iconType: 'moc' | 'integration' | 'audit' | 'compliance' | 'config' | 'document' | 'risk';
  actionText: string;
  tag: string;
  timestamp: string;
  targetTab: string;
  targetId: string;
  changeType?: string;
}

interface ActivityItem {
  id: string;
  userInitials: string;
  avatarBg: string;
  title: string;
  description: string;
  time: string;
  targetTab: string;
}

export const DashboardPage: React.FC<DashboardPageProps> = ({
  onNavigate,
  userName = 'Erdal',
  userRole = 'SAFETY_MANAGER',
  userId = 'usr_erdal_aydeniz'
}) => {
  const [activeMenuId, setActiveMenuId] = useState<string | null>(null);
  const [isCustomizeMode, setIsCustomizeMode] = useState<boolean>(false);
  const [showAddWidgetModal, setShowAddWidgetModal] = useState<boolean>(false);
  const [feedbackMsg, setFeedbackMsg] = useState<string | null>(null);

  // Activity Logs View All Modal & Filter State
  const [viewAllModalType, setViewAllModalType] = useState<'report' | 'system' | null>(null);
  const [viewAllSearch, setViewAllSearch] = useState<string>('');
  const [systemFilterDomain, setSystemFilterDomain] = useState<string>('ALL');

  // KPI Indicator Breakdown Modal State
  const [indicatorModal, setIndicatorModal] = useState<IndicatorModalState | null>(null);
  const [indicatorSearch, setIndicatorSearch] = useState<string>('');

  useEscapeKey(() => {
    if (indicatorModal) {
      setIndicatorModal(null);
      setIndicatorSearch('');
    } else if (viewAllModalType) {
      setViewAllModalType(null);
      setViewAllSearch('');
      setSystemFilterDomain('ALL');
    } else if (showAddWidgetModal) {
      setShowAddWidgetModal(false);
    }
  }, Boolean(indicatorModal || viewAllModalType || showAddWidgetModal));

  const handleOpenIndicator = (
    cardTitle: string,
    filterLabel: string,
    dotColorClass: string,
    subtitle: string,
    targetTab: string,
    defaultRecords: IndicatorRecordItem[] = []
  ) => {
    // Open immediately with the exact indicator records matching this indicator filter badge
    setIndicatorModal({
      cardTitle,
      filterLabel,
      dotColorClass,
      subtitle,
      targetTab,
      records: defaultRecords
    });
    setIndicatorSearch('');
  };

  // Active Layout state
  const [layout, setLayout] = useState<ConfiguredWidgetItem[]>(() =>
    getUserDashboardLayout(userId, userRole)
  );

  // Staged layout during edit mode
  const [stagedLayout, setStagedLayout] = useState<ConfiguredWidgetItem[]>([]);

  // Refresh layout when userId or userRole changes
  useEffect(() => {
    setLayout(getUserDashboardLayout(userId, userRole));
  }, [userId, userRole]);

  const handleStartCustomizing = () => {
    setStagedLayout(JSON.parse(JSON.stringify(layout)));
    setIsCustomizeMode(true);
  };

  const handleCancelCustomizing = () => {
    setStagedLayout([]);
    setIsCustomizeMode(false);
  };

  const handleSaveCustomizing = () => {
    saveUserDashboardLayout(userId, stagedLayout);
    setLayout(stagedLayout);
    setIsCustomizeMode(false);
    setFeedbackMsg('Your custom dashboard layout has been saved successfully.');
    setTimeout(() => setFeedbackMsg(null), 3500);

    // Also notify backend if preferences API is active
    try {
      dashboardService.savePreferences(
        {
          dashboardId: 'DEFAULT',
          custom_layout_json: JSON.stringify(stagedLayout)
        },
        userId
      ).catch(() => {});
    } catch {}
  };

  const handleResetToRoleDefault = () => {
    if (window.confirm('Reset dashboard to the default layout configured for your role?')) {
      resetUserDashboardLayout(userId);
      const roleDef = getRoleDefaultLayout(userRole);
      setLayout(roleDef);
      setStagedLayout(JSON.parse(JSON.stringify(roleDef)));
      setIsCustomizeMode(false);
      setFeedbackMsg(`Dashboard reset to ${userRole} default layout.`);
      setTimeout(() => setFeedbackMsg(null), 3500);
    }
  };

  // Reordering staged layout
  const handleMoveWidget = (index: number, direction: 'prev' | 'next') => {
    const targetIndex = direction === 'prev' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= stagedLayout.length) return;

    const copy = [...stagedLayout];
    const temp = copy[targetIndex];
    copy[targetIndex] = copy[index];
    copy[index] = temp;
    copy.forEach((item, idx) => {
      item.order = idx + 1;
    });
    setStagedLayout(copy);
  };

  // Size toggle staged layout
  const handleResizeWidget = (id: string, newSize: WidgetSize) => {
    setStagedLayout((prev) =>
      prev.map((item) => (item.id === id ? { ...item, size: newSize } : item))
    );
  };

  // Remove widget in staged layout
  const handleRemoveWidget = (id: string) => {
    setStagedLayout((prev) => {
      const filtered = prev.filter((item) => item.id !== id);
      filtered.forEach((item, idx) => {
        item.order = idx + 1;
      });
      return filtered;
    });
  };

  // Add widget from catalog to staged layout
  const handleAddWidgetToStaged = (catalogItem: WidgetCatalogItem) => {
    const newItem: ConfiguredWidgetItem = {
      id: `cfg-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`,
      widgetId: catalogItem.id,
      size: catalogItem.defaultSize,
      order: stagedLayout.length + 1
    };
    setStagedLayout([...stagedLayout, newItem]);
    setShowAddWidgetModal(false);
  };

  // Permitted widgets for current role
  const allowedWidgetsForUser = getAllowedWidgetsForRole(userRole);
  const activeWidgetIdsInStaged = stagedLayout.map((i) => i.widgetId);
  const availableToAdd = allowedWidgetsForUser.filter(
    (w) => !activeWidgetIdsInStaged.includes(w.id)
  );

  // Widget Catalog Search & Filter State
  const [widgetSearchQuery, setWidgetSearchQuery] = useState<string>('');
  const [widgetCategoryFilter, setWidgetCategoryFilter] = useState<'ALL' | 'METRIC' | 'TABLE' | 'FEED'>('ALL');

  const filteredAvailableWidgets = useMemo(() => {
    return availableToAdd.filter((w) => {
      if (widgetCategoryFilter !== 'ALL' && w.category !== widgetCategoryFilter) {
        return false;
      }
      if (!widgetSearchQuery.trim()) return true;
      const q = widgetSearchQuery.toLowerCase().trim();
      return (
        w.title.toLowerCase().includes(q) ||
        w.description.toLowerCase().includes(q) ||
        w.code.toLowerCase().includes(q) ||
        w.requiredModule.toLowerCase().includes(q) ||
        w.category.toLowerCase().includes(q)
      );
    });
  }, [availableToAdd, widgetSearchQuery, widgetCategoryFilter]);

  // Dynamic Operational Tables loaded directly from PostgreSQL via dashboardService
  const [myReports, setMyReports] = useState<MyReportRow[]>([]);
  const [waitingReports, setWaitingReports] = useState<WaitingReportRow[]>([]);
  const [openInvestigations, setOpenInvestigations] = useState<InvestigationRow[]>([]);
  const [upcomingAudits, setUpcomingAudits] = useState<AuditRow[]>([]);
  const [selectedReportIdForModal, setSelectedReportIdForModal] = useState<string | null>(null);
  const [selectedReportIdForEdit, setSelectedReportIdForEdit] = useState<string | null>(null);

  // Dynamic Activity Feeds loaded directly from PostgreSQL via dashboardService
  const [allReportActivityLogs, setAllReportActivityLogs] = useState<ReportActivityLogItem[]>([]);
  const [allSystemActivityLogs, setAllSystemActivityLogs] = useState<SystemActivityLogItem[]>([]);
  const [isLoadingDashboardData, setIsLoadingDashboardData] = useState<boolean>(true);

  // Dynamic SPI Alert & Missing Data states (Items 7, 8, 9)
  const [missingSpiAlerts, setMissingSpiAlerts] = useState<MissingIndicatorAlertItem[]>([]);
  const [missingSpiSummary, setMissingSpiSummary] = useState<{
    totalMissing: number;
    criticalMissing: number;
    isSmsPersonnel: boolean;
    cadenceCounts: { monthly: number; quarterly: number; annual: number };
  }>({
    totalMissing: 0,
    criticalMissing: 0,
    isSmsPersonnel: false,
    cadenceCounts: { monthly: 0, quarterly: 0, annual: 0 },
  });
  const [activeSpiThresholdAlerts, setActiveSpiThresholdAlerts] = useState<ActiveThresholdAlertItem[]>([]);
  const [activeThresholdSummary, setActiveThresholdSummary] = useState<{
    totalAlerts: number;
    criticalAlerts: number;
  }>({
    totalAlerts: 0,
    criticalAlerts: 0,
  });
  const [missingCadenceFilter, setMissingCadenceFilter] = useState<'ALL' | 'MONTHLY' | 'QUARTERLY' | 'ANNUAL'>('ALL');

  // Fetch real database records on mount or when session user changes
  useEffect(() => {
    let isMounted = true;
    setIsLoadingDashboardData(true);

    Promise.all([
      dashboardService.getDashboardTables(undefined, userId),
      dashboardService.getReportActivities(undefined, userId),
      dashboardService.getSystemActivities(),
      dashboardService.getMissingSpiAlerts(),
      dashboardService.getActiveSpiThresholdAlerts()
    ])
      .then(([tablesData, reportLogs, systemLogs, missingData, thresholdData]) => {
        if (!isMounted) return;

        if (tablesData) {
          if (Array.isArray(tablesData.myReports)) {
            setMyReports(tablesData.myReports);
          }
          if (Array.isArray(tablesData.waitingReports)) {
            setWaitingReports(tablesData.waitingReports);
          }
          if (Array.isArray(tablesData.openInvestigations)) {
            setOpenInvestigations(tablesData.openInvestigations);
          }
          if (Array.isArray(tablesData.upcomingAudits)) {
            setUpcomingAudits(tablesData.upcomingAudits);
          }
        }

        if (Array.isArray(reportLogs)) {
          setAllReportActivityLogs(reportLogs);
        }

        if (Array.isArray(systemLogs)) {
          setAllSystemActivityLogs(systemLogs);
        }

        if (missingData && Array.isArray(missingData.items)) {
          setMissingSpiAlerts(missingData.items);
          setMissingSpiSummary({
            totalMissing: missingData.totalMissing || missingData.items.length,
            criticalMissing: missingData.criticalMissing || 0,
            isSmsPersonnel: !!missingData.isSmsPersonnel,
            cadenceCounts: missingData.cadenceCounts || { monthly: 0, quarterly: 0, annual: 0 }
          });
        }

        if (thresholdData && Array.isArray(thresholdData.items)) {
          setActiveSpiThresholdAlerts(thresholdData.items);
          setActiveThresholdSummary({
            totalAlerts: thresholdData.totalAlerts || thresholdData.items.length,
            criticalAlerts: thresholdData.criticalAlerts || 0
          });
        }
      })
      .catch((err) => {
        console.error('Failed to load real-time database feeds and operational tables:', err);
      })
      .finally(() => {
        if (isMounted) setIsLoadingDashboardData(false);
      });

    return () => {
      isMounted = false;
    };
  }, [userId, userRole]);

  // Report Activity Feed: dynamic items retrieved from database for this user and organization
  const visibleReportActivityFeed = useMemo(() => {
    return allReportActivityLogs;
  }, [allReportActivityLogs]);

  // System Activity Feed: filtered by user's role permissions (Rule 10/26)
  // Even if user is not directly assigned, if they have permission to that module (e.g. 'moc'), they see it!
  const visibleSystemActivityFeed = useMemo(() => {
    return allSystemActivityLogs.filter((item) => {
      return isModuleAllowedForRole(userRole, item.domainModule);
    });
  }, [allSystemActivityLogs, userRole]);

  // Dynamic Operational Tables & Feeds Pagination (5 per page, scrollable)
  const {
    currentPage: myRepPage,
    setCurrentPage: setMyRepPage,
    pageSize: myRepPageSize,
    setPageSize: setMyRepPageSize,
    totalItems: totalMyRepItems,
    paginatedItems: paginatedMyReports,
  } = useTablePagination(myReports, 5);

  const {
    currentPage: waitRepPage,
    setCurrentPage: setWaitRepPage,
    pageSize: waitRepPageSize,
    setPageSize: setWaitRepPageSize,
    totalItems: totalWaitRepItems,
    paginatedItems: paginatedWaitingReports,
  } = useTablePagination(waitingReports, 5);

  const {
    currentPage: invPage,
    setCurrentPage: setInvPage,
    pageSize: invPageSize,
    setPageSize: setInvPageSize,
    totalItems: totalInvItems,
    paginatedItems: paginatedInvestigations,
  } = useTablePagination(openInvestigations, 5);

  const {
    currentPage: audPage,
    setCurrentPage: setAudPage,
    pageSize: audPageSize,
    setPageSize: setAudPageSize,
    totalItems: totalAudItems,
    paginatedItems: paginatedUpcomingAudits,
  } = useTablePagination(upcomingAudits, 5);

  const {
    currentPage: repFeedPage,
    setCurrentPage: setRepFeedPage,
    pageSize: repFeedPageSize,
    setPageSize: setRepFeedPageSize,
    totalItems: totalRepFeedItems,
    paginatedItems: paginatedReportFeed,
  } = useTablePagination(visibleReportActivityFeed, 5);

  const {
    currentPage: sysFeedPage,
    setCurrentPage: setSysFeedPage,
    pageSize: sysFeedPageSize,
    setPageSize: setSysFeedPageSize,
    totalItems: totalSysFeedItems,
    paginatedItems: paginatedSystemFeed,
  } = useTablePagination(visibleSystemActivityFeed, 5);

  const filteredMissingSpiAlerts = useMemo(() => {
    if (missingCadenceFilter === 'ALL') return missingSpiAlerts;
    return missingSpiAlerts.filter((item) => {
      const f = (item.frequency || '').toUpperCase();
      if (missingCadenceFilter === 'ANNUAL') return f === 'ANNUAL' || f === 'ANNUALLY';
      return f === missingCadenceFilter;
    });
  }, [missingSpiAlerts, missingCadenceFilter]);

  const {
    currentPage: missingSpiPage,
    setCurrentPage: setMissingSpiPage,
    pageSize: missingSpiPageSize,
    setPageSize: setMissingSpiPageSize,
    totalItems: totalMissingSpiItems,
    paginatedItems: paginatedMissingSpiAlerts,
  } = useTablePagination(filteredMissingSpiAlerts, 5);

  const {
    currentPage: threshAlertPage,
    setCurrentPage: setThreshAlertPage,
    pageSize: threshAlertPageSize,
    setPageSize: setThreshAlertPageSize,
    totalItems: totalThreshAlertItems,
    paginatedItems: paginatedThreshAlerts,
  } = useTablePagination(activeSpiThresholdAlerts, 5);

  const renderSystemIcon = (type: string) => {
    switch (type) {
      case 'moc':
        return <Layers size={14} color="#ffffff" />;
      case 'integration':
        return <Network size={14} color="#ffffff" />;
      case 'audit':
        return <ClipboardCheck size={14} color="#ffffff" />;
      case 'compliance':
        return <ShieldCheck size={14} color="#ffffff" />;
      case 'config':
        return <Settings size={14} color="#ffffff" />;
      case 'document':
        return <FileText size={14} color="#ffffff" />;
      case 'risk':
        return <AlertTriangle size={14} color="#ffffff" />;
      default:
        return <Activity size={14} color="#ffffff" />;
    }
  };

  const handleRowClick = (tab: string) => {
    if (onNavigate) {
      onNavigate(tab);
    }
  };

  const getSeverityBadgeClass = (severity: string) => {
    switch (severity) {
      case 'CRITICAL':
        return 'dash-badge-critical';
      case 'HIGH':
        return 'dash-badge-high';
      case 'MEDIUM':
        return 'dash-badge-medium';
      case 'LOW':
        return 'dash-badge-low';
      default:
        return 'dash-badge-normal';
    }
  };

  const getStatusBadgeClass = (color: string) => {
    switch (color) {
      case 'blue':
        return 'dash-status-blue';
      case 'amber':
        return 'dash-status-amber';
      case 'cyan':
        return 'dash-status-cyan';
      default:
        return 'dash-status-blue';
    }
  };

  // Active items to render
  const effectiveLayout = isCustomizeMode ? stagedLayout : layout;

  // Split into metric cards and major cards (tables & feeds)
  const metricItems = effectiveLayout.filter((item) => {
    const meta = WIDGET_CATALOG.find((w) => w.id === item.widgetId);
    return meta?.category === 'METRIC';
  });

  const contentItems = effectiveLayout.filter((item) => {
    const meta = WIDGET_CATALOG.find((w) => w.id === item.widgetId);
    return meta?.category !== 'METRIC';
  });

  // Render individual metric card
  const renderMetricCard = (item: ConfiguredWidgetItem, index: number) => {
    const meta = WIDGET_CATALOG.find((w) => w.id === item.widgetId);
    if (!meta) return null;

    let icon = <FileText size={18} />;
    let iconClass = 'icon-blue';
    let value = '12';
    let indicators = (
      <>
        <button
          type="button"
          className="dash-ind-btn"
          onClick={(e) => {
            e.stopPropagation();
            handleOpenIndicator(
              'My Reports',
              '3 Overdue',
              'dot-red',
              'Reports and assigned tasks that have passed their mandated regulatory SLA or due date.',
              'occurrences',
              OVERDUE_REPORTS_DATA
            );
          }}
          title="View 3 Overdue Reports"
        >
          <span className="dash-ind-dot dot-red" /> 3 Overdue
        </button>
        <button
          type="button"
          className="dash-ind-btn"
          onClick={(e) => {
            e.stopPropagation();
            handleOpenIndicator(
              'My Reports',
              '5 In Progress',
              'dot-blue',
              'Active safety and occurrence reports currently being analyzed, investigated or drafted.',
              'occurrences',
              IN_PROGRESS_REPORTS_DATA
            );
          }}
          title="View 5 In Progress Reports"
        >
          <span className="dash-ind-dot dot-blue" /> 5 In Progress
        </button>
        <button
          type="button"
          className="dash-ind-btn"
          onClick={(e) => {
            e.stopPropagation();
            handleOpenIndicator(
              'My Reports',
              '4 Pending',
              'dot-gray',
              'Submitted reports awaiting intake triage, departmental review, or verification.',
              'ingestion',
              PENDING_REPORTS_DATA
            );
          }}
          title="View 4 Pending Reports"
        >
          <span className="dash-ind-dot dot-gray" /> 4 Pending
        </button>
      </>
    );
    let navTarget = 'occurrences';

    if (item.widgetId === 'kpi_waiting_assessment') {
      icon = <Clock size={18} />;
      iconClass = 'icon-amber';
      value = '8';
      indicators = (
        <>
          <button
            type="button"
            className="dash-ind-btn"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenIndicator(
                'Reports Waiting Assessment',
                '3 High',
                'dot-red',
                'High & Critical risk reports awaiting triage and initial classification.',
                'ingestion',
                WAITING_ASSESS_HIGH_DATA
              );
            }}
            title="View 3 High Severity Reports"
          >
            <span className="dash-ind-dot dot-red" /> 3 High
          </button>
          <button
            type="button"
            className="dash-ind-btn"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenIndicator(
                'Reports Waiting Assessment',
                '3 Medium',
                'dot-amber',
                'Medium severity reports in intake queue awaiting risk scoring.',
                'ingestion',
                WAITING_ASSESS_MED_DATA
              );
            }}
            title="View 3 Medium Severity Reports"
          >
            <span className="dash-ind-dot dot-amber" /> 3 Medium
          </button>
          <button
            type="button"
            className="dash-ind-btn"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenIndicator(
                'Reports Waiting Assessment',
                '2 Low',
                'dot-green',
                'Low severity informational reports awaiting batch review.',
                'ingestion',
                WAITING_ASSESS_LOW_DATA
              );
            }}
            title="View 2 Low Severity Reports"
          >
            <span className="dash-ind-dot dot-green" /> 2 Low
          </button>
        </>
      );
      navTarget = 'ingestion';
    } else if (item.widgetId === 'kpi_feed_updates') {
      icon = <MessageSquare size={18} />;
      iconClass = 'icon-purple';
      value = '24';
      indicators = (
        <>
          <button
            type="button"
            className="dash-ind-btn"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenIndicator(
                'New Feed Updates',
                '8 Unread',
                'dot-purple',
                'Unread activity, attachments, comments and action assignments.',
                'tasks_actions',
                FEED_UPDATES_UNREAD_DATA
              );
            }}
            title="View 8 Unread Updates"
          >
            <span className="dash-ind-dot dot-purple" /> 8 Unread
          </button>
          <button
            type="button"
            className="dash-ind-btn"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenIndicator(
                'New Feed Updates',
                '16 Read',
                'dot-gray',
                'Historical and read feed notification logs.',
                'tasks_actions',
                FEED_UPDATES_READ_DATA
              );
            }}
            title="View 16 Read Updates"
          >
            <span className="dash-ind-dot dot-gray" /> 16 Read
          </button>
        </>
      );
      navTarget = 'tasks_actions';
    } else if (item.widgetId === 'kpi_overdue_actions') {
      icon = <AlertTriangle size={18} />;
      iconClass = 'icon-rose';
      value = '6';
      indicators = (
        <>
          <button
            type="button"
            className="dash-ind-btn"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenIndicator(
                'Overdue Actions',
                '2 Safety Actions',
                'dot-red',
                'Corrective and preventive safety action plans exceeding deadline.',
                'actions',
                OVERDUE_ACTIONS_SAFETY_DATA
              );
            }}
            title="View 2 Overdue Safety Actions"
          >
            <span className="dash-ind-dot dot-red" /> 2 Safety Actions
          </button>
          <button
            type="button"
            className="dash-ind-btn"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenIndicator(
                'Overdue Actions',
                '4 Investigations',
                'dot-gray',
                'Investigation milestones exceeding statutory reporting timelines.',
                'investigations',
                OVERDUE_ACTIONS_INVS_DATA
              );
            }}
            title="View 4 Overdue Investigations"
          >
            <span className="dash-ind-dot dot-gray" /> 4 Investigations
          </button>
        </>
      );
      navTarget = 'actions';
    } else if (item.widgetId === 'kpi_high_critical_risks') {
      icon = <ShieldAlert size={18} />;
      iconClass = 'icon-rose';
      value = '3';
      indicators = (
        <>
          <button
            type="button"
            className="dash-ind-btn"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenIndicator(
                'High & Critical Risks',
                '1 Red ALARP',
                'dot-red',
                'Unacceptable operational safety risks requiring executive action.',
                'risks',
                CRITICAL_RISKS_RED_DATA
              );
            }}
            title="View 1 Red ALARP Risk"
          >
            <span className="dash-ind-dot dot-red" /> 1 Red ALARP
          </button>
          <button
            type="button"
            className="dash-ind-btn"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenIndicator(
                'High & Critical Risks',
                '2 Amber',
                'dot-amber',
                'Tolerable risks with mandatory mitigation monitoring.',
                'risks',
                CRITICAL_RISKS_AMBER_DATA
              );
            }}
            title="View 2 Amber Risks"
          >
            <span className="dash-ind-dot dot-amber" /> 2 Amber
          </button>
        </>
      );
      navTarget = 'risks';
    } else if (item.widgetId === 'kpi_open_investigations') {
      icon = <Activity size={18} />;
      iconClass = 'icon-blue';
      value = '5';
      indicators = (
        <>
          <button
            type="button"
            className="dash-ind-btn"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenIndicator(
                'Open Investigations',
                '2 Inquiry',
                'dot-blue',
                'Incidents currently in initial evidence gathering and crew interview stage.',
                'investigations',
                OPEN_INVS_INQUIRY_DATA
              );
            }}
            title="View 2 Inquiry Phase Investigations"
          >
            <span className="dash-ind-dot dot-blue" /> 2 Inquiry
          </button>
          <button
            type="button"
            className="dash-ind-btn"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenIndicator(
                'Open Investigations',
                '2 RCA',
                'dot-amber',
                'Investigations applying 5-Why and Ishikawa root cause methods.',
                'investigations',
                OPEN_INVS_RCA_DATA
              );
            }}
            title="View 2 RCA Phase Investigations"
          >
            <span className="dash-ind-dot dot-amber" /> 2 RCA
          </button>
          <button
            type="button"
            className="dash-ind-btn"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenIndicator(
                'Open Investigations',
                '1 Final',
                'dot-green',
                'Completed investigations awaiting Safety Board sign-off.',
                'investigations',
                OPEN_INVS_FINAL_DATA
              );
            }}
            title="View 1 Final Review Investigation"
          >
            <span className="dash-ind-dot dot-green" /> 1 Final
          </button>
        </>
      );
      navTarget = 'investigations';
    } else if (item.widgetId === 'kpi_quality_findings') {
      icon = <CheckSquare size={18} />;
      iconClass = 'icon-amber';
      value = '4';
      indicators = (
        <>
          <button
            type="button"
            className="dash-ind-btn"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenIndicator(
                'Quality Non-Conformities',
                '1 Level 1',
                'dot-red',
                'Major regulatory non-conformances with immediate rectification order.',
                'findings',
                QUALITY_FINDINGS_L1_DATA
              );
            }}
            title="View 1 Level 1 Non-Conformance"
          >
            <span className="dash-ind-dot dot-red" /> 1 Level 1
          </button>
          <button
            type="button"
            className="dash-ind-btn"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenIndicator(
                'Quality Non-Conformities',
                '3 Level 2',
                'dot-amber',
                'Standard quality non-conformances pending corrective action approval.',
                'findings',
                QUALITY_FINDINGS_L2_DATA
              );
            }}
            title="View 3 Level 2 Non-Conformances"
          >
            <span className="dash-ind-dot dot-amber" /> 3 Level 2
          </button>
        </>
      );
      navTarget = 'findings';
    } else if (item.widgetId === 'kpi_compliance_score') {
      icon = <Shield size={18} />;
      iconClass = 'icon-blue';
      value = '98%';
      indicators = (
        <>
          <button
            type="button"
            className="dash-ind-btn"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenIndicator(
                'Compliance Monitoring',
                'EASA 98.2%',
                'dot-green',
                'EASA Part-ORO, Part-145 and Part-CAT audit compliance checkpoints.',
                'monitoring',
                COMPLIANCE_EASA_DATA
              );
            }}
            title="View EASA Compliance Audits"
          >
            <span className="dash-ind-dot dot-green" /> EASA 98.2%
          </button>
          <button
            type="button"
            className="dash-ind-btn"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenIndicator(
                'Compliance Monitoring',
                'ICAO 97.8%',
                'dot-blue',
                'ICAO Annex 19 and Annex 14 safety management fulfillment audit items.',
                'monitoring',
                COMPLIANCE_ICAO_DATA
              );
            }}
            title="View ICAO Compliance Audits"
          >
            <span className="dash-ind-dot dot-blue" /> ICAO 97.8%
          </button>
        </>
      );
      navTarget = 'monitoring';
    } else if (item.widgetId === 'kpi_moc_changes') {
      icon = <Layers size={18} />;
      iconClass = 'icon-purple';
      value = '3';
      indicators = (
        <>
          <button
            type="button"
            className="dash-ind-btn"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenIndicator(
                'Active MOC Changes',
                '1 Impact',
                'dot-blue',
                'Changes currently undergoing formal safety risk and hazard assessment.',
                'moc',
                MOC_IMPACT_DATA
              );
            }}
            title="View 1 Impact Assessment MOC"
          >
            <span className="dash-ind-dot dot-blue" /> 1 Impact
          </button>
          <button
            type="button"
            className="dash-ind-btn"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenIndicator(
                'Active MOC Changes',
                '1 Approval',
                'dot-amber',
                'Changes with completed risk assessments awaiting board signature.',
                'moc',
                MOC_APPROVAL_DATA
              );
            }}
            title="View 1 Pending Approval MOC"
          >
            <span className="dash-ind-dot dot-amber" /> 1 Approval
          </button>
        </>
      );
      navTarget = 'moc';
    } else if (item.widgetId === 'kpi_system_telemetry') {
      icon = <Settings size={18} />;
      iconClass = 'icon-gray';
      value = '99.98%';
      indicators = (
        <>
          <button
            type="button"
            className="dash-ind-btn"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenIndicator(
                'Integration Telemetry',
                '1.4k msgs/h',
                'dot-green',
                'Real-time inbound data stream rates from ACARS, FDM and METAR.',
                'integration_attempts',
                TELEMETRY_STREAM_DATA
              );
            }}
            title="View Stream Telemetry"
          >
            <span className="dash-ind-dot dot-green" /> 1.4k msgs/h
          </button>
          <button
            type="button"
            className="dash-ind-btn"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenIndicator(
                'Integration Telemetry',
                '0 Dead-Letter',
                'dot-gray',
                'Integration Hub dead-letter queue and failed payload buffer.',
                'integration_attempts',
                TELEMETRY_DLQ_DATA
              );
            }}
            title="View Dead-Letter Buffer"
          >
            <span className="dash-ind-dot dot-gray" /> 0 Dead-Letter
          </button>
        </>
      );
      navTarget = 'integration_attempts';
    } else if (item.widgetId === 'kpi_hazard_register') {
      icon = <AlertTriangle size={18} />;
      iconClass = 'icon-amber';
      value = '19';
      indicators = (
        <>
          <button type="button" className="dash-ind-btn" onClick={(e) => { e.stopPropagation(); onNavigate && onNavigate('hazards'); }}>
            <span className="dash-ind-dot dot-red" /> 4 High
          </button>
          <button type="button" className="dash-ind-btn" onClick={(e) => { e.stopPropagation(); onNavigate && onNavigate('hazards'); }}>
            <span className="dash-ind-dot dot-amber" /> 9 Med
          </button>
          <button type="button" className="dash-ind-btn" onClick={(e) => { e.stopPropagation(); onNavigate && onNavigate('hazards'); }}>
            <span className="dash-ind-dot dot-blue" /> 6 Low
          </button>
        </>
      );
      navTarget = 'hazards';
    } else if (item.widgetId === 'kpi_open_capas') {
      icon = <CheckSquare size={18} />;
      iconClass = 'icon-blue';
      value = '8';
      indicators = (
        <>
          <button type="button" className="dash-ind-btn" onClick={(e) => { e.stopPropagation(); onNavigate && onNavigate('capas'); }}>
            <span className="dash-ind-dot dot-red" /> 2 Overdue
          </button>
          <button type="button" className="dash-ind-btn" onClick={(e) => { e.stopPropagation(); onNavigate && onNavigate('capas'); }}>
            <span className="dash-ind-dot dot-blue" /> 4 In Progress
          </button>
          <button type="button" className="dash-ind-btn" onClick={(e) => { e.stopPropagation(); onNavigate && onNavigate('capas'); }}>
            <span className="dash-ind-dot dot-green" /> 2 Verified
          </button>
        </>
      );
      navTarget = 'capas';
    } else if (item.widgetId === 'kpi_spi_spt_performance') {
      icon = <Activity size={18} />;
      iconClass = 'icon-green';
      value = '96.4%';
      indicators = (
        <>
          <button type="button" className="dash-ind-btn" onClick={(e) => { e.stopPropagation(); onNavigate && onNavigate('performance'); }}>
            <span className="dash-ind-dot dot-green" /> 12 In Target
          </button>
          <button type="button" className="dash-ind-btn" onClick={(e) => { e.stopPropagation(); onNavigate && onNavigate('performance'); }}>
            <span className="dash-ind-dot dot-amber" /> 1 Warning
          </button>
        </>
      );
      navTarget = 'performance';
    } else if (item.widgetId === 'kpi_missing_spi_data') {
      icon = <AlertTriangle size={18} />;
      iconClass = missingSpiSummary.criticalMissing > 0 ? 'icon-red' : 'icon-amber';
      value = String(missingSpiSummary.totalMissing);
      indicators = (
        <>
          <button type="button" className="dash-ind-btn" onClick={(e) => { e.stopPropagation(); onNavigate && onNavigate('performance'); }}>
            <span className="dash-ind-dot dot-red" /> {missingSpiSummary.cadenceCounts.monthly} Mo
          </button>
          <button type="button" className="dash-ind-btn" onClick={(e) => { e.stopPropagation(); onNavigate && onNavigate('performance'); }}>
            <span className="dash-ind-dot dot-amber" /> {missingSpiSummary.cadenceCounts.quarterly} Qtr
          </button>
          <button type="button" className="dash-ind-btn" onClick={(e) => { e.stopPropagation(); onNavigate && onNavigate('performance'); }}>
            <span className="dash-ind-dot dot-blue" /> {missingSpiSummary.cadenceCounts.annual} Ann
          </button>
        </>
      );
      navTarget = 'performance';
    } else if (item.widgetId === 'kpi_spi_threshold_alerts') {
      icon = <ShieldAlert size={18} />;
      const criticalCount = activeThresholdSummary.criticalAlerts;
      iconClass = criticalCount > 0 ? 'icon-red' : activeThresholdSummary.totalAlerts > 0 ? 'icon-amber' : 'icon-green';
      value = String(activeThresholdSummary.totalAlerts);
      indicators = (
        <>
          <button type="button" className="dash-ind-btn" onClick={(e) => { e.stopPropagation(); onNavigate && onNavigate('performance'); }}>
            <span className="dash-ind-dot dot-red" /> {criticalCount} Critical
          </button>
          <button type="button" className="dash-ind-btn" onClick={(e) => { e.stopPropagation(); onNavigate && onNavigate('performance'); }}>
            <span className="dash-ind-dot dot-amber" /> {activeThresholdSummary.totalAlerts - criticalCount} Warning
          </button>
        </>
      );
      navTarget = 'performance';
    } else if (item.widgetId === 'kpi_upcoming_audits') {
      icon = <Calendar size={18} />;
      iconClass = 'icon-amber';
      value = '5';
      indicators = (
        <>
          <button type="button" className="dash-ind-btn" onClick={(e) => { e.stopPropagation(); onNavigate && onNavigate('audits'); }}>
            <span className="dash-ind-dot dot-red" /> 1 Overdue
          </button>
          <button type="button" className="dash-ind-btn" onClick={(e) => { e.stopPropagation(); onNavigate && onNavigate('audits'); }}>
            <span className="dash-ind-dot dot-blue" /> 4 Scheduled
          </button>
        </>
      );
      navTarget = 'audits';
    } else if (item.widgetId === 'kpi_training_expirations') {
      icon = <Plane size={18} />;
      iconClass = 'icon-purple';
      value = '7';
      indicators = (
        <>
          <button type="button" className="dash-ind-btn" onClick={(e) => { e.stopPropagation(); onNavigate && onNavigate('training'); }}>
            <span className="dash-ind-dot dot-red" /> 3 Due &lt;15d
          </button>
          <button type="button" className="dash-ind-btn" onClick={(e) => { e.stopPropagation(); onNavigate && onNavigate('training'); }}>
            <span className="dash-ind-dot dot-amber" /> 4 Due &lt;30d
          </button>
        </>
      );
      navTarget = 'training';
    } else if (item.widgetId === 'kpi_asset_maintenance') {
      icon = <Building size={18} />;
      iconClass = 'icon-blue';
      value = '4';
      indicators = (
        <>
          <button type="button" className="dash-ind-btn" onClick={(e) => { e.stopPropagation(); onNavigate && onNavigate('assets'); }}>
            <span className="dash-ind-dot dot-amber" /> 1 Check Due
          </button>
          <button type="button" className="dash-ind-btn" onClick={(e) => { e.stopPropagation(); onNavigate && onNavigate('assets'); }}>
            <span className="dash-ind-dot dot-blue" /> 3 Calibrations
          </button>
        </>
      );
      navTarget = 'assets';
    } else if (item.widgetId === 'kpi_pending_approvals') {
      icon = <GitBranch size={18} />;
      iconClass = 'icon-amber';
      value = '3';
      indicators = (
        <>
          <button type="button" className="dash-ind-btn" onClick={(e) => { e.stopPropagation(); onNavigate && onNavigate('workflows'); }}>
            <span className="dash-ind-dot dot-red" /> 1 MOC Sign-off
          </button>
          <button type="button" className="dash-ind-btn" onClick={(e) => { e.stopPropagation(); onNavigate && onNavigate('workflows'); }}>
            <span className="dash-ind-dot dot-blue" /> 2 Manuals
          </button>
        </>
      );
      navTarget = 'workflows';
    } else if (item.widgetId === 'kpi_safety_surveys') {
      icon = <ClipboardCheck size={18} />;
      iconClass = 'icon-green';
      value = '84.2%';
      indicators = (
        <>
          <button type="button" className="dash-ind-btn" onClick={(e) => { e.stopPropagation(); onNavigate && onNavigate('surveys'); }}>
            <span className="dash-ind-dot dot-green" /> 2 Campaigns
          </button>
          <button type="button" className="dash-ind-btn" onClick={(e) => { e.stopPropagation(); onNavigate && onNavigate('surveys'); }}>
            <span className="dash-ind-dot dot-blue" /> 142 Submissions
          </button>
        </>
      );
      navTarget = 'surveys';
    } else if (item.widgetId === 'kpi_regulatory_obligations') {
      icon = <ShieldCheck size={18} />;
      iconClass = 'icon-blue';
      value = '2';
      indicators = (
        <>
          <button type="button" className="dash-ind-btn" onClick={(e) => { e.stopPropagation(); onNavigate && onNavigate('obligations'); }}>
            <span className="dash-ind-dot dot-green" /> 1 Exemption Active
          </button>
          <button type="button" className="dash-ind-btn" onClick={(e) => { e.stopPropagation(); onNavigate && onNavigate('obligations'); }}>
            <span className="dash-ind-dot dot-blue" /> 1 Notice
          </button>
        </>
      );
      navTarget = 'obligations';
    } else if (item.widgetId === 'kpi_flight_occurrences') {
      icon = <AlertTriangle size={18} />;
      iconClass = 'icon-rose';
      value = '14';
      indicators = (
        <>
          <button type="button" className="dash-ind-btn" onClick={(e) => { e.stopPropagation(); onNavigate && onNavigate('occurrences'); }}>
            <span className="dash-ind-dot dot-red" /> 3 Mandatory (MOR)
          </button>
          <button type="button" className="dash-ind-btn" onClick={(e) => { e.stopPropagation(); onNavigate && onNavigate('occurrences'); }}>
            <span className="dash-ind-dot dot-blue" /> 11 Internal
          </button>
        </>
      );
      navTarget = 'occurrences';
    }

    const spanStyle = item.size === '2-col' ? { gridColumn: 'span 2' } : item.size === 'full' ? { gridColumn: '1 / -1' } : {};

    return (
      <div
        key={item.id}
        className={`dash-kpi-card ${isCustomizeMode ? 'customizing' : ''}`}
        style={spanStyle}
        onClick={() => !isCustomizeMode && onNavigate && onNavigate(navTarget)}
      >
        {isCustomizeMode && (
          <div className="dash-widget-edit-bar" onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <button
                type="button"
                className="dash-edit-btn"
                onClick={() => handleMoveWidget(index, 'prev')}
                disabled={index === 0}
                title="Move Left"
              >
                <ArrowLeft size={13} />
              </button>
              <button
                type="button"
                className="dash-edit-btn"
                onClick={() => handleMoveWidget(index, 'next')}
                disabled={index === stagedLayout.length - 1}
                title="Move Right"
              >
                <ArrowRight size={13} />
              </button>
              <select
                value={item.size}
                onChange={(e) => handleResizeWidget(item.id, e.target.value as WidgetSize)}
                className="dash-edit-select"
              >
                <option value="1-col">1 Col</option>
                <option value="2-col">2 Col</option>
                <option value="full">Full</option>
              </select>
            </div>
            <button
              type="button"
              className="dash-edit-btn-remove"
              onClick={() => handleRemoveWidget(item.id)}
              title="Remove Card"
            >
              <Trash2 size={13} />
            </button>
          </div>
        )}

        <div className="dash-kpi-top">
          <div className={`dash-kpi-icon-wrap ${iconClass}`}>{icon}</div>
          <span className="dash-kpi-title">{meta.title}</span>
        </div>
        <div className="dash-kpi-number">{value}</div>
        <div className="dash-kpi-indicators">{indicators}</div>
      </div>
    );
  };

  // Render major content card (Table or Feed)
  const renderContentCard = (item: ConfiguredWidgetItem, index: number) => {
    const meta = WIDGET_CATALOG.find((w) => w.id === item.widgetId);
    if (!meta) return null;

    const spanStyle = item.size === 'full' ? { gridColumn: '1 / -1' } : {};

    const renderToolbar = () => {
      if (!isCustomizeMode) return null;
      return (
        <div className="dash-widget-edit-bar" onClick={(e) => e.stopPropagation()}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontSize: '11px', fontWeight: 700, color: '#475569' }}>
              {meta.title} ({meta.code})
            </span>
            <button
              type="button"
              className="dash-edit-btn"
              onClick={() => handleMoveWidget(index, 'prev')}
              disabled={index === 0}
              title="Move Up"
            >
              <ArrowUp size={13} />
            </button>
            <button
              type="button"
              className="dash-edit-btn"
              onClick={() => handleMoveWidget(index, 'next')}
              disabled={index === stagedLayout.length - 1}
              title="Move Down"
            >
              <ArrowDown size={13} />
            </button>
            <select
              value={item.size}
              onChange={(e) => handleResizeWidget(item.id, e.target.value as WidgetSize)}
              className="dash-edit-select"
            >
              <option value="2-col">Half Width (2-col)</option>
              <option value="full">Full Width (1/1)</option>
            </select>
          </div>
          <button
            type="button"
            className="dash-edit-btn-remove"
            onClick={() => handleRemoveWidget(item.id)}
            title="Remove Widget"
          >
            <Trash2 size={13} />
          </button>
        </div>
      );
    };

    if (item.widgetId === 'table_my_reports') {
      return (
        <div key={item.id} className="dash-card" style={spanStyle}>
          {renderToolbar()}
          <div className="dash-card-header">
            <div className="dash-card-header-left">
              <div className="dash-card-icon-wrap icon-gray">
                <FileText size={18} />
              </div>
              <div>
                <h3 className="dash-card-title">My Reports</h3>
                <p className="dash-card-subtitle">Reports assigned to you</p>
              </div>
            </div>
            <button
              type="button"
              className="dash-view-all-btn"
              onClick={() => onNavigate && onNavigate('occurrences')}
            >
              <span>View all</span>
              <ArrowRight size={13} />
            </button>
          </div>

          <div className="dash-table-wrap">
            <table className="dash-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Title</th>
                  <th>Status</th>
                  <th>Due Date</th>
                  <th style={{ width: '32px' }}></th>
                </tr>
              </thead>
              <tbody>
                {paginatedMyReports.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'center', padding: '24px', color: '#64748b' }}>
                      {isLoadingDashboardData ? 'Loading your assigned reports...' : 'No reports assigned to you.'}
                    </td>
                  </tr>
                ) : (
                  paginatedMyReports.map((row) => (
                    <tr
                      key={row.id}
                      onClick={() => setSelectedReportIdForModal(row.id)}
                      className="dash-clickable-row"
                    >
                      <td className="dash-cell-code">{row.id}</td>
                      <td className="dash-cell-title">{row.title}</td>
                      <td>
                        <span className={`dash-status-pill ${getStatusBadgeClass(row.statusColor)}`}>
                          {row.status === 'SUBMITTED' || row.status === 'WAITING_ASSESSMENT' ? 'Waiting Assessment' : row.status}
                        </span>
                      </td>
                      <td>
                        <div className="dash-duedate-wrap">
                          <span className="dash-duedate-date">{row.dueDate}</span>
                          <span
                            className={`dash-duedate-sub ${
                              row.isUrgent ? 'urgent' : row.daysLeft <= 4 ? 'soon' : ''
                            }`}
                          >
                            {row.daysLeft} days left
                          </span>
                        </div>
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          className="dash-row-more-btn"
                          onClick={() => setActiveMenuId(activeMenuId === row.id ? null : row.id)}
                          title="Options"
                        >
                          <MoreVertical size={16} />
                        </button>
                        {activeMenuId === row.id && (
                          <div className="dash-row-menu">
                            <button
                              onClick={() => {
                                setActiveMenuId(null);
                                setSelectedReportIdForModal(row.id);
                              }}
                            >
                              Open Details
                            </button>
                            {(row.status === 'Waiting Assessment' || row.status === 'SUBMITTED' || row.status === 'WAITING_ASSESSMENT') && (
                              <button
                                onClick={() => {
                                  setActiveMenuId(null);
                                  setSelectedReportIdForEdit(row.id);
                                }}
                              >
                                ✏️ Edit Report
                              </button>
                            )}
                            <button onClick={() => setActiveMenuId(null)}>Mark as Reviewed</button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <TablePagination
            currentPage={myRepPage}
            totalItems={totalMyRepItems}
            pageSize={myRepPageSize}
            onPageChange={setMyRepPage}
            onPageSizeChange={setMyRepPageSize}
            pageSizeOptions={[5, 10, 20]}
            itemLabel="reports"
          />
        </div>
      );
    }

    if (item.widgetId === 'table_waiting_assessment') {
      return (
        <div key={item.id} className="dash-card" style={spanStyle}>
          {renderToolbar()}
          <div className="dash-card-header">
            <div className="dash-card-header-left">
              <div className="dash-card-icon-wrap icon-amber-soft">
                <Clock size={18} />
              </div>
              <div>
                <h3 className="dash-card-title">Reports Waiting Assessment</h3>
                <p className="dash-card-subtitle">New reports awaiting SMS assessment</p>
              </div>
            </div>
            <button
              type="button"
              className="dash-view-all-btn"
              onClick={() => onNavigate && onNavigate('ingestion')}
            >
              <span>View all</span>
              <ArrowRight size={13} />
            </button>
          </div>

          <div className="dash-table-wrap">
            <table className="dash-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Title</th>
                  <th>Kind of Report</th>
                  <th>Severity</th>
                  <th>Received</th>
                  <th style={{ width: '32px' }}></th>
                </tr>
              </thead>
              <tbody>
                {paginatedWaitingReports.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ textAlign: 'center', padding: '24px', color: '#64748b' }}>
                      {isLoadingDashboardData ? 'Loading intake triage queue...' : 'No reports waiting assessment.'}
                    </td>
                  </tr>
                ) : (
                  paginatedWaitingReports.map((row) => (
                    <tr
                      key={row.id}
                      onClick={() => setSelectedReportIdForModal(row.id)}
                      className="dash-clickable-row"
                    >
                      <td className="dash-cell-code">{row.id}</td>
                      <td className="dash-cell-title">{row.title}</td>
                      <td className="dash-cell-kind">{row.kindOfReport}</td>
                      <td>
                        <span className={`dash-severity-pill ${getSeverityBadgeClass(row.severity)}`}>
                          {row.severity}
                        </span>
                      </td>
                      <td className="dash-cell-received">{row.received}</td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          className="dash-row-more-btn"
                          onClick={() => setActiveMenuId(activeMenuId === row.id ? null : row.id)}
                          title="Options"
                        >
                          <MoreVertical size={16} />
                        </button>
                        {activeMenuId === row.id && (
                          <div className="dash-row-menu">
                            <button
                              onClick={() => {
                                setActiveMenuId(null);
                                setSelectedReportIdForModal(row.id);
                              }}
                            >
                              Inspect Details
                            </button>
                            <button
                              onClick={() => {
                                setActiveMenuId(null);
                                if (onNavigate) {
                                  onNavigate('ingestion', { reportId: row.id });
                                }
                              }}
                            >
                              Assess & Validate
                            </button>
                            <button onClick={() => setActiveMenuId(null)}>Assign Investigator</button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <TablePagination
            currentPage={waitRepPage}
            totalItems={totalWaitRepItems}
            pageSize={waitRepPageSize}
            onPageChange={setWaitRepPage}
            onPageSizeChange={setWaitRepPageSize}
            pageSizeOptions={[5, 10, 20]}
            itemLabel="reports"
          />
        </div>
      );
    }

    if (item.widgetId === 'table_missing_spi_data') {
      return (
        <div key={item.id} className="dash-card" style={spanStyle}>
          {renderToolbar()}
          <div className="dash-card-header">
            <div className="dash-card-header-left">
              <div className="dash-card-icon-wrap icon-amber-soft">
                <AlertTriangle size={18} />
              </div>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <h3 className="dash-card-title">Missing SPI Data Entries</h3>
                  <span style={{
                    fontSize: '11px',
                    padding: '2px 8px',
                    borderRadius: '12px',
                    fontWeight: 600,
                    background: missingSpiSummary.isSmsPersonnel ? '#eff6ff' : '#f0fdf4',
                    color: missingSpiSummary.isSmsPersonnel ? '#1e40af' : '#166534',
                    border: missingSpiSummary.isSmsPersonnel ? '1px solid #bfdbfe' : '1px solid #bbf7d0'
                  }}>
                    {missingSpiSummary.isSmsPersonnel ? '🏢 Company-Wide SMS' : '👤 Role Scoped'}
                  </span>
                </div>
                <p className="dash-card-subtitle">
                  {missingSpiSummary.isSmsPersonnel
                    ? 'All company indicators with missing data for elapsed periods'
                    : 'Your department / provider indicators pending required data entry'}
                </p>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              {(['ALL', 'MONTHLY', 'QUARTERLY', 'ANNUAL'] as const).map((cad) => (
                <button
                  key={cad}
                  type="button"
                  onClick={() => {
                    setMissingCadenceFilter(cad);
                    setMissingSpiPage(1);
                  }}
                  style={{
                    padding: '4px 8px',
                    fontSize: '11px',
                    fontWeight: 600,
                    borderRadius: '6px',
                    border: missingCadenceFilter === cad ? '1px solid #2563eb' : '1px solid #cbd5e1',
                    background: missingCadenceFilter === cad ? '#2563eb' : '#f8fafc',
                    color: missingCadenceFilter === cad ? '#ffffff' : '#475569',
                    cursor: 'pointer',
                  }}
                >
                  {cad === 'ALL' ? `All (${missingSpiAlerts.length})` :
                   cad === 'MONTHLY' ? `Mo (${missingSpiSummary.cadenceCounts.monthly})` :
                   cad === 'QUARTERLY' ? `Qtr (${missingSpiSummary.cadenceCounts.quarterly})` :
                   `Ann (${missingSpiSummary.cadenceCounts.annual})`}
                </button>
              ))}
              <button
                type="button"
                className="dash-view-all-btn"
                onClick={() => onNavigate && onNavigate('performance')}
              >
                <span>Performance</span>
                <ArrowRight size={13} />
              </button>
            </div>
          </div>

          <div className="dash-table-wrap">
            <table className="dash-table">
              <thead>
                <tr>
                  <th>Indicator Code</th>
                  <th>Indicator Name</th>
                  <th>Cadence</th>
                  <th>Owner / Data Provider</th>
                  <th>Missing Elapsed Periods</th>
                  <th>Your Role</th>
                  <th style={{ textAlign: 'right' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {paginatedMissingSpiAlerts.length === 0 ? (
                  <tr>
                    <td colSpan={7} style={{ textAlign: 'center', padding: '24px', color: '#16a34a', fontWeight: 500 }}>
                      ✓ All required SPI data entries are up-to-date for your scope!
                    </td>
                  </tr>
                ) : (
                  paginatedMissingSpiAlerts.map((row) => (
                    <tr
                      key={row.id}
                      className="dash-clickable-row"
                      onClick={() => onNavigate && onNavigate('performance', { indicatorId: row.id })}
                    >
                      <td className="dash-cell-code" style={{ fontWeight: 700 }}>{row.code}</td>
                      <td className="dash-cell-title">
                        <div style={{ fontWeight: 600, color: '#0f172a' }}>{row.name}</div>
                        <div style={{ fontSize: '11px', color: '#64748b' }}>{row.cadenceDescription}</div>
                      </td>
                      <td>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                          <span style={{
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontWeight: 700,
                            display: 'inline-block',
                            width: 'fit-content',
                            background: row.frequency.includes('MONTH') ? '#e0f2fe' : row.frequency.includes('QUART') ? '#fef3c7' : '#f3e8ff',
                            color: row.frequency.includes('MONTH') ? '#0369a1' : row.frequency.includes('QUART') ? '#92400e' : '#6b21a8'
                          }}>
                            {row.frequency}
                          </span>
                          {row.cadenceEvaluation?.statusTag && (
                            <span style={{ fontSize: '10px', fontWeight: 600, color: row.severity === 'CRITICAL' ? '#b91c1c' : '#b45309' }}>
                              {row.cadenceEvaluation.statusTag}
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div style={{ fontSize: '12px', fontWeight: 600, color: '#334155' }}>
                          🏢 {row.ownerDepartment || 'SMS Central'}
                        </div>
                        {row.dataProviderDepartment && (
                          <div style={{ fontSize: '11px', color: '#64748b' }}>
                            Data: {row.dataProviderDepartment}
                          </div>
                        )}
                      </td>
                      <td>
                        {row.cadenceEvaluation?.evaluationType === 'ANNUAL_YEAR_IN_PROGRESS' ? (
                          <span style={{
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontWeight: 600,
                            background: '#f3e8ff',
                            color: '#6b21a8',
                            border: '1px solid #d8b4fe'
                          }}>
                            ⏳ Yıl Sonu Kapanışı Bekleniyor
                          </span>
                        ) : row.cadenceEvaluation?.evaluationType === 'CURRENT_MONTH_PENDING' ? (
                          <span style={{
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontWeight: 600,
                            background: '#eff6ff',
                            color: '#1d4ed8',
                            border: '1px solid #bfdbfe'
                          }}>
                            📅 Cari Ay Kapanış Bekliyor ({row.missingPeriods[0]})
                          </span>
                        ) : (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                            {row.missingPeriods.slice(0, 3).map((p) => (
                              <span key={p} style={{
                                padding: '1px 6px',
                                borderRadius: '4px',
                                fontSize: '11px',
                                fontWeight: 600,
                                background: '#fee2e2',
                                color: '#991b1b',
                                border: '1px solid #fecdd3'
                              }}>
                                {p}
                              </span>
                            ))}
                            {row.missingPeriods.length > 3 && (
                              <span style={{ fontSize: '11px', color: '#dc2626', fontWeight: 600, alignSelf: 'center' }}>
                                +{row.missingPeriods.length - 3} more
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                      <td>
                        <span style={{
                          padding: '2px 8px',
                          borderRadius: '12px',
                          fontSize: '10px',
                          fontWeight: 700,
                          background: row.userRoleInIndicator === 'DATA_PROVIDER' ? '#f0fdf4' : row.userRoleInIndicator === 'OWNER' ? '#eff6ff' : '#f1f5f9',
                          color: row.userRoleInIndicator === 'DATA_PROVIDER' ? '#166534' : row.userRoleInIndicator === 'OWNER' ? '#1e40af' : '#475569'
                        }}>
                          {row.userRoleInIndicator}
                        </span>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <button
                          type="button"
                          className="btn-primary"
                          style={{
                            padding: '4px 12px',
                            fontSize: '11px',
                            fontWeight: 600,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            onNavigate && onNavigate('performance', { indicatorId: row.id });
                          }}
                        >
                          <span>{row.userRoleInIndicator === 'STAKEHOLDER' ? 'View' : 'Fast Entry'}</span>
                          <ArrowRight size={12} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <TablePagination
            currentPage={missingSpiPage}
            totalItems={totalMissingSpiItems}
            pageSize={missingSpiPageSize}
            onPageChange={setMissingSpiPage}
            onPageSizeChange={setMissingSpiPageSize}
            pageSizeOptions={[5, 10, 20]}
            itemLabel="missing indicators"
          />
        </div>
      );
    }

    if (item.widgetId === 'table_spi_threshold_alerts') {
      return (
        <div key={item.id} className="dash-card" style={spanStyle}>
          {renderToolbar()}
          <div className="dash-card-header">
            <div className="dash-card-header-left">
              <div className="dash-card-icon-wrap icon-red-soft">
                <ShieldAlert size={18} />
              </div>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <h3 className="dash-card-title">Active SPI Threshold Breaches & Alerts</h3>
                  <span style={{
                    fontSize: '11px',
                    padding: '2px 8px',
                    borderRadius: '12px',
                    fontWeight: 600,
                    background: missingSpiSummary.isSmsPersonnel ? '#eff6ff' : '#f0fdf4',
                    color: missingSpiSummary.isSmsPersonnel ? '#1e40af' : '#166534',
                    border: missingSpiSummary.isSmsPersonnel ? '1px solid #bfdbfe' : '1px solid #bbf7d0'
                  }}>
                    {missingSpiSummary.isSmsPersonnel ? '🏢 Company-Wide SMS' : '👤 Role Scoped'}
                  </span>
                </div>
                <p className="dash-card-subtitle">
                  {missingSpiSummary.isSmsPersonnel
                    ? 'Company-wide active safety target and sigma threshold deviations'
                    : 'Active breaches affecting your department and stakeholder areas'}
                </p>
              </div>
            </div>
            <button
              type="button"
              className="dash-view-all-btn"
              onClick={() => onNavigate && onNavigate('performance')}
            >
              <span>View in SPI Matrix</span>
              <ArrowRight size={13} />
            </button>
          </div>

          <div className="dash-table-wrap">
            <table className="dash-table">
              <thead>
                <tr>
                  <th>Indicator Code</th>
                  <th>Indicator Name</th>
                  <th>Period</th>
                  <th>Measured</th>
                  <th>Methodology</th>
                  <th>Alert Level</th>
                  <th>Your Role</th>
                  <th style={{ textAlign: 'right' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {paginatedThreshAlerts.length === 0 ? (
                  <tr>
                    <td colSpan={8} style={{ textAlign: 'center', padding: '24px', color: '#16a34a', fontWeight: 500 }}>
                      ✓ Zero active SPI threshold breaches detected. Performance is within acceptable safety bounds!
                    </td>
                  </tr>
                ) : (
                  paginatedThreshAlerts.map((row) => (
                    <tr
                      key={`${row.id}-${row.period}`}
                      className="dash-clickable-row"
                      onClick={() => onNavigate && onNavigate('performance', { indicatorId: row.id })}
                    >
                      <td className="dash-cell-code" style={{ fontWeight: 700 }}>{row.code}</td>
                      <td className="dash-cell-title">
                        <div style={{ fontWeight: 600, color: '#0f172a' }}>{row.name}</div>
                        <div style={{ fontSize: '11px', color: '#64748b' }}>Dept: {row.ownerDepartment}</div>
                      </td>
                      <td style={{ fontWeight: 600, fontSize: '12px' }}>{row.period}</td>
                      <td>
                        <span style={{ fontWeight: 700, fontSize: '13px', color: row.severity === 'CRITICAL' ? '#b91c1c' : '#c2410c' }}>
                          {row.measuredValue} {row.unit}
                        </span>
                      </td>
                      <td>
                        <span style={{
                          padding: '2px 8px',
                          borderRadius: '4px',
                          fontSize: '10px',
                          fontWeight: 700,
                          background: row.thresholdMethodology === 'SPECIFIC_TARGET' ? '#e0e7ff' : '#f1f5f9',
                          color: row.thresholdMethodology === 'SPECIFIC_TARGET' ? '#3730a3' : '#334155'
                        }}>
                          {row.thresholdMethodology === 'SPECIFIC_TARGET' ? '🎯 Specific Target' : '📐 3-Sigma'}
                        </span>
                      </td>
                      <td>
                        <span style={{
                          padding: '3px 8px',
                          borderRadius: '6px',
                          fontSize: '11px',
                          fontWeight: 700,
                          background: row.severity === 'CRITICAL' ? '#fee2e2' : '#ffedd5',
                          color: row.severity === 'CRITICAL' ? '#991b1b' : '#9a3412',
                          border: row.severity === 'CRITICAL' ? '1px solid #fca5a5' : '1px solid #fdba74'
                        }}>
                          {row.alertLevel}
                        </span>
                      </td>
                      <td>
                        <span style={{
                          padding: '2px 8px',
                          borderRadius: '12px',
                          fontSize: '10px',
                          fontWeight: 700,
                          background: row.userRoleInIndicator === 'DATA_PROVIDER' ? '#f0fdf4' : row.userRoleInIndicator === 'OWNER' ? '#eff6ff' : row.userRoleInIndicator === 'STAKEHOLDER' ? '#faf5ff' : '#f1f5f9',
                          color: row.userRoleInIndicator === 'DATA_PROVIDER' ? '#166534' : row.userRoleInIndicator === 'OWNER' ? '#1e40af' : row.userRoleInIndicator === 'STAKEHOLDER' ? '#7e22ce' : '#475569',
                          border: row.userRoleInIndicator === 'STAKEHOLDER' ? '1px solid #e9d5ff' : 'none'
                        }}>
                          {row.userRoleInIndicator === 'STAKEHOLDER' ? '👀 STAKEHOLDER' : row.userRoleInIndicator}
                        </span>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <button
                          type="button"
                          className="btn-secondary"
                          style={{
                            padding: '4px 10px',
                            fontSize: '11px',
                            fontWeight: 600,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            onNavigate && onNavigate('performance', { indicatorId: row.id });
                          }}
                        >
                          <span>Review</span>
                          <ArrowRight size={12} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <TablePagination
            currentPage={threshAlertPage}
            totalItems={totalThreshAlertItems}
            pageSize={threshAlertPageSize}
            onPageChange={setThreshAlertPage}
            onPageSizeChange={setThreshAlertPageSize}
            pageSizeOptions={[5, 10, 20]}
            itemLabel="alert indicators"
          />
        </div>
      );
    }

    if (item.widgetId === 'table_open_investigations') {
      return (
        <div key={item.id} className="dash-card" style={spanStyle}>
          {renderToolbar()}
          <div className="dash-card-header">
            <div className="dash-card-header-left">
              <div className="dash-card-icon-wrap icon-blue">
                <Activity size={18} />
              </div>
              <div>
                <h3 className="dash-card-title">Active Investigations Register</h3>
                <p className="dash-card-subtitle">Formal incident inquiries in flight</p>
              </div>
            </div>
            <button
              type="button"
              className="dash-view-all-btn"
              onClick={() => onNavigate && onNavigate('investigations')}
            >
              <span>View all</span>
              <ArrowRight size={13} />
            </button>
          </div>

          <div className="dash-table-wrap">
            <table className="dash-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Title</th>
                  <th>Lead Investigator</th>
                  <th>Phase</th>
                  <th>Target Date</th>
                </tr>
              </thead>
              <tbody>
                {paginatedInvestigations.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'center', padding: '24px', color: '#64748b' }}>
                      {isLoadingDashboardData ? 'Loading investigations from database...' : 'No active investigations found.'}
                    </td>
                  </tr>
                ) : (
                  paginatedInvestigations.map((inv) => (
                    <tr
                      key={inv.id}
                      onClick={() => handleRowClick('investigations')}
                      className="dash-clickable-row"
                    >
                      <td className="dash-cell-code">{inv.id}</td>
                      <td className="dash-cell-title">{inv.title}</td>
                      <td className="dash-cell-kind">{inv.leadInvestigator}</td>
                      <td>
                        <span className="dash-status-pill dash-status-amber">{inv.phase}</span>
                      </td>
                      <td className="dash-cell-received">{inv.targetDate}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <TablePagination
            currentPage={invPage}
            totalItems={totalInvItems}
            pageSize={invPageSize}
            onPageChange={setInvPage}
            onPageSizeChange={setInvPageSize}
            pageSizeOptions={[5, 10, 20]}
            itemLabel="investigations"
          />
        </div>
      );
    }

    if (item.widgetId === 'table_audit_schedule') {
      return (
        <div key={item.id} className="dash-card" style={spanStyle}>
          {renderToolbar()}
          <div className="dash-card-header">
            <div className="dash-card-header-left">
              <div className="dash-card-icon-wrap icon-amber">
                <ClipboardCheck size={18} />
              </div>
              <div>
                <h3 className="dash-card-title">Quality Audits & Inspections</h3>
                <p className="dash-card-subtitle">Upcoming audit programme schedule</p>
              </div>
            </div>
            <button
              type="button"
              className="dash-view-all-btn"
              onClick={() => onNavigate && onNavigate('audits')}
            >
              <span>View all</span>
              <ArrowRight size={13} />
            </button>
          </div>

          <div className="dash-table-wrap">
            <table className="dash-table">
              <thead>
                <tr>
                  <th>Audit ID</th>
                  <th>Programme / Title</th>
                  <th>Scope Type</th>
                  <th>Lead Auditor</th>
                  <th>Scheduled Date</th>
                </tr>
              </thead>
              <tbody>
                {paginatedUpcomingAudits.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'center', padding: '24px', color: '#64748b' }}>
                      {isLoadingDashboardData ? 'Loading audit programme schedule...' : 'No upcoming audits found.'}
                    </td>
                  </tr>
                ) : (
                  paginatedUpcomingAudits.map((aud) => (
                    <tr
                      key={aud.id}
                      onClick={() => handleRowClick('audits')}
                      className="dash-clickable-row"
                    >
                      <td className="dash-cell-code">{aud.id}</td>
                      <td className="dash-cell-title">{aud.title}</td>
                      <td className="dash-cell-kind">{aud.type}</td>
                      <td className="dash-cell-kind">{aud.auditor}</td>
                      <td className="dash-cell-received">{aud.scheduledDate}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <TablePagination
            currentPage={audPage}
            totalItems={totalAudItems}
            pageSize={audPageSize}
            onPageChange={setAudPage}
            onPageSizeChange={setAudPageSize}
            pageSizeOptions={[5, 10, 20]}
            itemLabel="audits"
          />
        </div>
      );
    }

    if (item.widgetId === 'feed_report_activity') {
      return (
        <div key={item.id} className="dash-card" style={spanStyle}>
          {renderToolbar()}
          <div className="dash-card-header">
            <div className="dash-card-header-left">
              <div className="dash-card-icon-wrap icon-blue-soft">
                <MessageSquare size={18} />
              </div>
              <div>
                <h3 className="dash-card-title">Report Activity Feed</h3>
                <p className="dash-card-subtitle">Report logs and updates directly involving you</p>
              </div>
            </div>
            <button
              type="button"
              className="dash-view-all-btn"
              onClick={() => setViewAllModalType('report')}
            >
              <span>View all</span>
              <ArrowRight size={13} />
            </button>
          </div>

          <div className="dash-feed-timeline-list">
            {paginatedReportFeed.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '24px', color: '#64748b' }}>
                {isLoadingDashboardData ? 'Loading report activity feed...' : 'No report activities found.'}
              </div>
            ) : (
              paginatedReportFeed.map((log, index) => (
                <div key={log.id} className="dash-feed-item-row">
                  <div className="dash-feed-indicator">
                    <div
                      className="dash-feed-avatar"
                      style={{ backgroundColor: log.avatarBg }}
                    >
                      {log.actorInitials}
                    </div>
                    {index < paginatedReportFeed.length - 1 && (
                      <div className="dash-feed-timeline-line" />
                    )}
                  </div>

                  <div className="dash-feed-body">
                    <div className="dash-feed-header-line">
                      <span className="dash-feed-actor">
                        {log.actorName}{' '}
                        <span className="dash-feed-dept">({log.actorDepartment})</span>
                      </span>
                      <span className="dash-feed-timestamp">{log.timestamp}</span>
                    </div>

                    <p className="dash-feed-action-text">{log.actionText}</p>

                    <button
                      type="button"
                      className="dash-feed-tag-pill"
                      onClick={() => onNavigate && onNavigate(log.targetTab, { id: log.targetId })}
                      title={`Open ${log.tag}`}
                    >
                      <span>{log.tag}</span>
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
          <TablePagination
            currentPage={repFeedPage}
            totalItems={totalRepFeedItems}
            pageSize={repFeedPageSize}
            onPageChange={setRepFeedPage}
            onPageSizeChange={setRepFeedPageSize}
            pageSizeOptions={[5, 10, 20]}
            itemLabel="updates"
          />
        </div>
      );
    }

    if (item.widgetId === 'feed_system_activity') {
      return (
        <div key={item.id} className="dash-card" style={spanStyle}>
          {renderToolbar()}
          <div className="dash-card-header">
            <div className="dash-card-header-left">
              <div className="dash-card-icon-wrap icon-gray">
                <Settings size={18} />
              </div>
              <div>
                <h3 className="dash-card-title">System Activity Feed</h3>
                <p className="dash-card-subtitle">General system, audit, compliance and configuration logs</p>
              </div>
            </div>
            <button
              type="button"
              className="dash-view-all-btn"
              onClick={() => setViewAllModalType('system')}
            >
              <span>View all</span>
              <ArrowRight size={13} />
            </button>
          </div>

          <div className="dash-feed-timeline-list">
            {paginatedSystemFeed.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '24px', color: '#64748b' }}>
                {isLoadingDashboardData ? 'Loading system activity feed...' : 'No system activities found.'}
              </div>
            ) : (
              paginatedSystemFeed.map((log, index) => (
                <div key={log.id} className="dash-feed-item-row">
                  <div className="dash-feed-indicator">
                    <div
                      className="dash-feed-icon-node"
                      style={{ backgroundColor: log.iconColor }}
                    >
                      {renderSystemIcon(log.iconType)}
                    </div>
                    {index < paginatedSystemFeed.length - 1 && (
                      <div className="dash-feed-timeline-line" />
                    )}
                  </div>

                  <div className="dash-feed-body">
                    <div className="dash-feed-header-line">
                      <span className="dash-feed-actor">{log.domainTitle}</span>
                      <span className="dash-feed-timestamp">{log.timestamp}</span>
                    </div>

                    <p className="dash-feed-action-text">{log.actionText}</p>

                    <button
                      type="button"
                      className="dash-feed-tag-pill"
                      onClick={() => onNavigate && onNavigate(log.targetTab, { id: log.targetId })}
                      title={`Open ${log.tag}`}
                    >
                      <span>{log.tag}</span>
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
          <TablePagination
            currentPage={sysFeedPage}
            totalItems={totalSysFeedItems}
            pageSize={sysFeedPageSize}
            onPageChange={setSysFeedPage}
            onPageSizeChange={setSysFeedPageSize}
            pageSizeOptions={[5, 10, 20]}
            itemLabel="logs"
          />
        </div>
      );
    }

    if (item.widgetId === 'table_open_capas') {
      const capasRows = [
        { id: 'CAPA-2026-081', title: 'Dual Engine Anti-Ice Advisory Follow-up', dept: 'Flight Operations', sev: 'CRITICAL', due: '5 days left', status: 'UNDER_REVIEW' },
        { id: 'CAPA-2026-084', title: 'Ramp Foreign Object Debris Control Action', dept: 'Ground Operations', sev: 'HIGH', due: '12 days left', status: 'IN_PROGRESS' },
        { id: 'CAPA-2026-089', title: 'Part-145 Torque Wrench Recalibration Protocol', dept: 'Maintenance', sev: 'HIGH', due: '18 days left', status: 'IN_PROGRESS' },
        { id: 'CAPA-2026-092', title: 'Cabin Baggage Stowing Compliance Measure', dept: 'Cabin Operations', sev: 'MEDIUM', due: '25 days left', status: 'VERIFIED' },
        { id: 'CAPA-2026-097', title: 'Winter Operation De-Icing Training Update', dept: 'Flight Crew Training', sev: 'LOW', due: '30 days left', status: 'PENDING' },
      ];
      return (
        <div key={item.id} className="dash-card" style={spanStyle}>
          {renderToolbar()}
          <div className="dash-card-header">
            <div className="dash-card-header-left">
              <div className="dash-card-icon-wrap icon-blue">
                <CheckSquare size={18} />
              </div>
              <div>
                <h3 className="dash-card-title">Corrective Actions (CAPA) Register</h3>
                <p className="dash-card-subtitle">Formal corrective and preventive action plans</p>
              </div>
            </div>
            <button
              type="button"
              className="dash-view-all-btn"
              onClick={() => onNavigate && onNavigate('capas')}
            >
              <span>View all</span>
              <ArrowRight size={13} />
            </button>
          </div>
          <div className="dash-table-wrap">
            <table className="dash-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Title</th>
                  <th>Responsible</th>
                  <th>Severity</th>
                  <th>Due Date</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {capasRows.map((r) => (
                  <tr key={r.id} onClick={() => onNavigate && onNavigate('capas')} className="dash-clickable-row">
                    <td className="dash-cell-code">{r.id}</td>
                    <td className="dash-cell-title">{r.title}</td>
                    <td className="dash-cell-kind">{r.dept}</td>
                    <td>
                      <span className={`dash-severity-pill ${getSeverityBadgeClass(r.sev)}`}>{r.sev}</span>
                    </td>
                    <td className="dash-cell-received">{r.due}</td>
                    <td>
                      <span className="dash-status-pill dash-status-amber">{r.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    if (item.widgetId === 'table_top_hazards') {
      const hazardRows = [
        { id: 'HAZ-2026-012', title: 'Runway Incursion during Low Visibility CAT III', cat: 'Aerodrome & Runway', risk: '5C (High ALARP)', owner: 'Flight Ops Lead' },
        { id: 'HAZ-2026-019', title: 'Crew Fatigue during consecutive night rotations', cat: 'Crew Fatigue & Rostering', risk: '4B (High ALARP)', owner: 'Crew Planning Lead' },
        { id: 'HAZ-2026-024', title: 'Bird Strike Risk in Terminal Maneuvering Area', cat: 'Environmental & Wildlife', risk: '3C (Medium)', owner: 'Safety Officer' },
        { id: 'HAZ-2026-031', title: 'Hydraulic System Leakage on Extended Range Twin', cat: 'Technical & Airframe', risk: '3B (Medium)', owner: 'Maintenance Lead' },
        { id: 'HAZ-2026-037', title: 'Ground Handling Tug Towbar Shearpin Failure', cat: 'Ramp & Ground Services', risk: '3A (Low)', owner: 'Station Manager' },
      ];
      return (
        <div key={item.id} className="dash-card" style={spanStyle}>
          {renderToolbar()}
          <div className="dash-card-header">
            <div className="dash-card-header-left">
              <div className="dash-card-icon-wrap icon-amber">
                <AlertTriangle size={18} />
              </div>
              <div>
                <h3 className="dash-card-title">Top Operational Hazards Register</h3>
                <p className="dash-card-subtitle">Highest-rated operational hazards under mitigation</p>
              </div>
            </div>
            <button
              type="button"
              className="dash-view-all-btn"
              onClick={() => onNavigate && onNavigate('hazards')}
            >
              <span>View all</span>
              <ArrowRight size={13} />
            </button>
          </div>
          <div className="dash-table-wrap">
            <table className="dash-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Hazard Statement</th>
                  <th>Category</th>
                  <th>Risk Rating</th>
                  <th>Owner</th>
                </tr>
              </thead>
              <tbody>
                {hazardRows.map((r) => (
                  <tr key={r.id} onClick={() => onNavigate && onNavigate('hazards')} className="dash-clickable-row">
                    <td className="dash-cell-code">{r.id}</td>
                    <td className="dash-cell-title">{r.title}</td>
                    <td className="dash-cell-kind">{r.cat}</td>
                    <td>
                      <span className={`dash-severity-pill ${r.risk.includes('High') ? 'dash-sev-critical' : 'dash-sev-medium'}`}>
                        {r.risk}
                      </span>
                    </td>
                    <td className="dash-cell-received">{r.owner}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    if (item.widgetId === 'table_quality_findings') {
      const findingRows = [
        { id: 'FND-2026-004', title: 'Part-145 Tool Calibration Tagging Discrepancy', audit: 'AUD-2026-012', level: 'Level 2', dept: 'Technical Services', due: '14 days' },
        { id: 'FND-2026-007', title: 'Flight Operations Manual Rev 14 Distribution Record Missing', audit: 'AUD-2026-015', level: 'Level 2', dept: 'Flight Operations', due: '21 days' },
        { id: 'FND-2026-011', title: 'Ramp Safety Vest Non-Compliance during Night Turnaround', audit: 'AUD-2026-018', level: 'Level 3', dept: 'Ground Handling', due: '28 days' },
        { id: 'FND-2026-014', title: 'Dangerous Goods Acceptance Checklist Outdated Version', audit: 'AUD-2026-020', level: 'Level 2', dept: 'Cargo Operations', due: '35 days' },
      ];
      return (
        <div key={item.id} className="dash-card" style={spanStyle}>
          {renderToolbar()}
          <div className="dash-card-header">
            <div className="dash-card-header-left">
              <div className="dash-card-icon-wrap icon-rose">
                <ShieldAlert size={18} />
              </div>
              <div>
                <h3 className="dash-card-title">Non-Conformities & Findings Register</h3>
                <p className="dash-card-subtitle">Audit observations requiring corrective action plans</p>
              </div>
            </div>
            <button
              type="button"
              className="dash-view-all-btn"
              onClick={() => onNavigate && onNavigate('findings')}
            >
              <span>View all</span>
              <ArrowRight size={13} />
            </button>
          </div>
          <div className="dash-table-wrap">
            <table className="dash-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Summary</th>
                  <th>Audit Ref</th>
                  <th>Level</th>
                  <th>Department</th>
                  <th>Target Date</th>
                </tr>
              </thead>
              <tbody>
                {findingRows.map((r) => (
                  <tr key={r.id} onClick={() => onNavigate && onNavigate('findings')} className="dash-clickable-row">
                    <td className="dash-cell-code">{r.id}</td>
                    <td className="dash-cell-title">{r.title}</td>
                    <td className="dash-cell-kind">{r.audit}</td>
                    <td>
                      <span className={`dash-status-pill ${r.level === 'Level 1' ? 'dash-status-red' : 'dash-status-amber'}`}>
                        {r.level}
                      </span>
                    </td>
                    <td className="dash-cell-received">{r.dept}</td>
                    <td className="dash-cell-received">{r.due}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    if (item.widgetId === 'table_training_currency') {
      const trainRows = [
        { id: 'STF-1042', name: 'Capt. Selim Kaya', role: 'A320 Fleet', req: 'CRM Recurrent Simulator Check', expiry: '12 days left', status: 'Expiring Soon' },
        { id: 'STF-2088', name: 'F/O Can Demir', role: 'B737 Fleet', req: 'CAT III Low Visibility Recurrency', expiry: '18 days left', status: 'Expiring Soon' },
        { id: 'STF-3011', name: 'Eng. Murat Arslan', role: 'Part-145 Avionics', req: 'Human Factors & Safety Refresher', expiry: '24 days left', status: 'Upcoming' },
        { id: 'STF-4029', name: 'S/A Zeynep Yilmaz', role: 'Cabin Crew', req: 'Annual Emergency & First Aid Drill', expiry: '29 days left', status: 'Upcoming' },
      ];
      return (
        <div key={item.id} className="dash-card" style={spanStyle}>
          {renderToolbar()}
          <div className="dash-card-header">
            <div className="dash-card-header-left">
              <div className="dash-card-icon-wrap icon-purple">
                <Plane size={18} />
              </div>
              <div>
                <h3 className="dash-card-title">Crew Qualifications & Currency Expirations</h3>
                <p className="dash-card-subtitle">Personnel qualifications expiring within 30 days</p>
              </div>
            </div>
            <button
              type="button"
              className="dash-view-all-btn"
              onClick={() => onNavigate && onNavigate('training')}
            >
              <span>View all</span>
              <ArrowRight size={13} />
            </button>
          </div>
          <div className="dash-table-wrap">
            <table className="dash-table">
              <thead>
                <tr>
                  <th>Staff ID</th>
                  <th>Name</th>
                  <th>Role / Fleet</th>
                  <th>Requirement</th>
                  <th>Expiry Date</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {trainRows.map((r) => (
                  <tr key={r.id} onClick={() => onNavigate && onNavigate('training')} className="dash-clickable-row">
                    <td className="dash-cell-code">{r.id}</td>
                    <td className="dash-cell-title">{r.name}</td>
                    <td className="dash-cell-kind">{r.role}</td>
                    <td>{r.req}</td>
                    <td className="dash-cell-received">{r.expiry}</td>
                    <td>
                      <span className={`dash-status-pill ${r.status === 'Expiring Soon' ? 'dash-status-red' : 'dash-status-amber'}`}>
                        {r.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    if (item.widgetId === 'table_pending_approvals') {
      const apprRows = [
        { id: 'APR-2026-015', title: 'MOC-2026-04: Electronic Flight Bag App Update v4.2', type: 'Management of Change', req: 'Tech Lead', age: '2 days ago', action: 'Sign-off' },
        { id: 'APR-2026-018', title: 'SOP Revision: Winter De-icing Ground Procedure', type: 'Manual Publication', req: 'Ground Safety Lead', age: '3 days ago', action: 'Review' },
        { id: 'APR-2026-022', title: 'Special Flight Permit Deviation: Ferry Flight TC-LMA', type: 'Regulatory Exemption', req: 'Maintenance Director', age: '4 days ago', action: 'Sign-off' },
      ];
      return (
        <div key={item.id} className="dash-card" style={spanStyle}>
          {renderToolbar()}
          <div className="dash-card-header">
            <div className="dash-card-header-left">
              <div className="dash-card-icon-wrap icon-amber">
                <GitBranch size={18} />
              </div>
              <div>
                <h3 className="dash-card-title">Workflow Governance & Approval Requests</h3>
                <p className="dash-card-subtitle">Pending multi-level sign-offs for safety cases</p>
              </div>
            </div>
            <button
              type="button"
              className="dash-view-all-btn"
              onClick={() => onNavigate && onNavigate('workflows')}
            >
              <span>View all</span>
              <ArrowRight size={13} />
            </button>
          </div>
          <div className="dash-table-wrap">
            <table className="dash-table">
              <thead>
                <tr>
                  <th>Request ID</th>
                  <th>Title</th>
                  <th>Workflow Type</th>
                  <th>Requester</th>
                  <th>Age</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {apprRows.map((r) => (
                  <tr key={r.id} onClick={() => onNavigate && onNavigate('workflows')} className="dash-clickable-row">
                    <td className="dash-cell-code">{r.id}</td>
                    <td className="dash-cell-title">{r.title}</td>
                    <td className="dash-cell-kind">{r.type}</td>
                    <td className="dash-cell-received">{r.req}</td>
                    <td className="dash-cell-received">{r.age}</td>
                    <td>
                      <span className="dash-status-pill dash-status-blue">{r.action}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    if (item.widgetId === 'table_asset_maintenance') {
      const assetRows = [
        { id: 'TC-LMA', desc: 'Airbus A320neo (MSN 9122)', stn: 'LTFM / Istanbul', event: 'A-Check Airframe Inspection', due: '4 days left', status: 'Scheduled' },
        { id: 'TC-LMB', desc: 'Boeing 737-800 (MSN 38411)', stn: 'LTBA / Ataturk', event: 'Line Service & Brake Wear Check', due: '7 days left', status: 'In Service' },
        { id: 'CAL-8841', desc: 'Digital Torque Wrench Calibrator', stn: 'SAW Hangar', event: 'Annual Laboratory Recalibration', due: '11 days left', status: 'Valid' },
        { id: 'TC-LMC', desc: 'Airbus A321neo (MSN 10044)', stn: 'LTFJ / Sabiha', event: '500h Engine Borescope Inspection', due: '16 days left', status: 'Scheduled' },
      ];
      return (
        <div key={item.id} className="dash-card" style={spanStyle}>
          {renderToolbar()}
          <div className="dash-card-header">
            <div className="dash-card-header-left">
              <div className="dash-card-icon-wrap icon-blue">
                <Building size={18} />
              </div>
              <div>
                <h3 className="dash-card-title">Fleet Assets & Calibration Register</h3>
                <p className="dash-card-subtitle">Scheduled line maintenance and tool calibration validity</p>
              </div>
            </div>
            <button
              type="button"
              className="dash-view-all-btn"
              onClick={() => onNavigate && onNavigate('assets')}
            >
              <span>View all</span>
              <ArrowRight size={13} />
            </button>
          </div>
          <div className="dash-table-wrap">
            <table className="dash-table">
              <thead>
                <tr>
                  <th>Asset ID</th>
                  <th>Description</th>
                  <th>Station</th>
                  <th>Next Event</th>
                  <th>Due Date</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {assetRows.map((r) => (
                  <tr key={r.id} onClick={() => onNavigate && onNavigate('assets')} className="dash-clickable-row">
                    <td className="dash-cell-code">{r.id}</td>
                    <td className="dash-cell-title">{r.desc}</td>
                    <td className="dash-cell-kind">{r.stn}</td>
                    <td>{r.event}</td>
                    <td className="dash-cell-received">{r.due}</td>
                    <td>
                      <span className="dash-status-pill dash-status-green">{r.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    if (item.widgetId === 'feed_audit_findings') {
      const findingsFeed = [
        { id: 'ff-1', domain: 'Technical Audit AUD-2026-012', time: '1 hour ago', text: 'Level 2 non-conformity verified on battery charging station ventilation.', tag: 'FND-2026-004', color: '#ef4444' },
        { id: 'ff-2', domain: 'Flight Operations AUD-2026-015', time: '4 hours ago', text: 'Finding issued regarding electronic flight bag revision currency.', tag: 'FND-2026-007', color: '#f59e0b' },
        { id: 'ff-3', domain: 'Ground Handling AUD-2026-018', time: 'Yesterday', text: 'Level 3 observation recorded during pushback crew communication review.', tag: 'FND-2026-011', color: '#3b82f6' },
      ];
      return (
        <div key={item.id} className="dash-card" style={spanStyle}>
          {renderToolbar()}
          <div className="dash-card-header">
            <div className="dash-card-header-left">
              <div className="dash-card-icon-wrap icon-purple">
                <ShieldCheck size={18} />
              </div>
              <div>
                <h3 className="dash-card-title">Quality & Audit Findings Feed</h3>
                <p className="dash-card-subtitle">Live stream of newly recorded audit non-conformities</p>
              </div>
            </div>
            <button
              type="button"
              className="dash-view-all-btn"
              onClick={() => onNavigate && onNavigate('findings')}
            >
              <span>View all</span>
              <ArrowRight size={13} />
            </button>
          </div>
          <div className="dash-feed-timeline-list">
            {findingsFeed.map((log) => (
              <div key={log.id} className="dash-feed-item-row">
                <div className="dash-feed-indicator">
                  <div className="dash-feed-icon-node" style={{ backgroundColor: log.color }}>
                    <ShieldAlert size={14} color="#ffffff" />
                  </div>
                </div>
                <div className="dash-feed-body">
                  <div className="dash-feed-header-line">
                    <span className="dash-feed-actor">{log.domain}</span>
                    <span className="dash-feed-timestamp">{log.time}</span>
                  </div>
                  <p className="dash-feed-action-text">{log.text}</p>
                  <button
                    type="button"
                    className="dash-feed-tag-pill"
                    onClick={() => onNavigate && onNavigate('findings')}
                  >
                    <span>{log.tag}</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="dashboard-content-wrapper">
      {/* 1. Breadcrumb + Customization Toggle Button */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div className="dash-breadcrumb">
          <span className="dash-breadcrumb-item">Overview</span>
          <span className="dash-breadcrumb-sep">/</span>
          <span className="dash-breadcrumb-current">Dashboard</span>
        </div>

        {!isCustomizeMode && (
          <button
            type="button"
            onClick={handleStartCustomizing}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 14px',
              borderRadius: '8px',
              border: '1px solid #cbd5e1',
              backgroundColor: '#ffffff',
              color: '#334155',
              fontSize: '12.5px',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.15s ease'
            }}
            title="Customize your dashboard layout"
          >
            <SlidersHorizontal size={14} />
            <span>Customize Dashboard</span>
          </button>
        )}
      </div>

      {/* Feedback banner */}
      {feedbackMsg && (
        <div
          style={{
            backgroundColor: '#f0fdf4',
            border: '1px solid #bbf7d0',
            borderRadius: '10px',
            padding: '10px 16px',
            color: '#166534',
            fontSize: '13px',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            animation: 'fadeIn 0.15s ease'
          }}
        >
          <CheckCircle2 size={16} />
          <span>{feedbackMsg}</span>
        </div>
      )}

      {/* Customization Mode Toolbar */}
      {isCustomizeMode && (
        <div
          style={{
            backgroundColor: '#0f172a',
            color: '#ffffff',
            borderRadius: '14px',
            padding: '16px 20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '12px',
            boxShadow: '0 10px 25px -5px rgba(15, 23, 42, 0.2)',
            animation: 'fadeIn 0.15s ease'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <SlidersHorizontal size={18} color="#60a5fa" />
            <div>
              <div style={{ fontSize: '14px', fontWeight: 700 }}>
                Dashboard Customization Mode
              </div>
              <div style={{ fontSize: '11.5px', color: '#94a3b8' }}>
                Role: <b>{userRole}</b> • Reorder, resize, or add widgets. Only widgets authorized for your role are accessible.
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button
              type="button"
              onClick={() => {
                setWidgetSearchQuery('');
                setWidgetCategoryFilter('ALL');
                setShowAddWidgetModal(true);
              }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '5px',
                padding: '7px 12px',
                borderRadius: '6px',
                backgroundColor: '#2563eb',
                border: 'none',
                color: '#ffffff',
                fontSize: '12px',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              <Plus size={14} />
              <span>Add Widget</span>
            </button>

            <button
              type="button"
              onClick={handleResetToRoleDefault}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '5px',
                padding: '7px 12px',
                borderRadius: '6px',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                color: '#ffffff',
                fontSize: '12px',
                fontWeight: 600,
                cursor: 'pointer'
              }}
              title="Reset to role default layout"
            >
              <RotateCcw size={13} />
              <span>Reset to Default</span>
            </button>

            <button
              type="button"
              onClick={handleCancelCustomizing}
              style={{
                padding: '7px 12px',
                borderRadius: '6px',
                backgroundColor: 'transparent',
                border: '1px solid #475569',
                color: '#cbd5e1',
                fontSize: '12px',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>

            <button
              type="button"
              onClick={handleSaveCustomizing}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '5px',
                padding: '7px 16px',
                borderRadius: '6px',
                backgroundColor: '#10b981',
                border: 'none',
                color: '#ffffff',
                fontSize: '12px',
                fontWeight: 700,
                cursor: 'pointer'
              }}
            >
              <Save size={14} />
              <span>Save Layout</span>
            </button>
          </div>
        </div>
      )}

      {/* 2. Greeting Header */}
      <div className="dash-hero-section">
        <div className="dash-greeting-wrap">
          <h1 className="dash-greeting-title">
            Hello, {userName} <span className="dash-wave">👋</span>
          </h1>
          <p className="dash-greeting-sub">
            Monitor safety, quality and compliance. Take action for a safer tomorrow.
          </p>
        </div>
      </div>

      {/* Dynamic SPI Warning / Alert Banner */}
      {(missingSpiSummary.totalMissing > 0 || activeThresholdSummary.totalAlerts > 0) && (
        <div style={{
          margin: '0 0 20px 0',
          padding: '16px 20px',
          borderRadius: '10px',
          background: activeThresholdSummary.criticalAlerts > 0 ? '#fff1f2' : '#fffbeb',
          border: activeThresholdSummary.criticalAlerts > 0 ? '1px solid #fecdd3' : '1px solid #fde68a',
          boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '8px',
              background: activeThresholdSummary.criticalAlerts > 0 ? '#fee2e2' : '#fef3c7',
              color: activeThresholdSummary.criticalAlerts > 0 ? '#dc2626' : '#d97706',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              <AlertTriangle size={22} />
            </div>
            <div>
              <div style={{
                fontSize: '14px',
                fontWeight: 700,
                color: activeThresholdSummary.criticalAlerts > 0 ? '#991b1b' : '#92400e'
              }}>
                Safety Performance Attention Required ({missingSpiSummary.isSmsPersonnel ? 'Company-Wide SMS Oversight' : 'Your Scope / Department'})
              </div>
              <div style={{ fontSize: '12px', color: '#475569', marginTop: '2px' }}>
                {activeThresholdSummary.totalAlerts > 0 && (
                  <span style={{ marginRight: '14px', fontWeight: 600, color: '#b91c1c' }}>
                    🚨 {activeThresholdSummary.totalAlerts} Active SPI Threshold Breach(es)
                  </span>
                )}
                {missingSpiSummary.totalMissing > 0 && (
                  <span style={{ fontWeight: 600, color: '#d97706' }}>
                    ⏳ {missingSpiSummary.totalMissing} SPI(s) with missing data entries ({missingSpiSummary.cadenceCounts.monthly} Mo, {missingSpiSummary.cadenceCounts.quarterly} Qtr, {missingSpiSummary.cadenceCounts.annual} Ann)
                  </span>
                )}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button
              type="button"
              className="btn-primary"
              style={{
                padding: '8px 16px',
                fontSize: '12px',
                fontWeight: 700,
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                background: activeThresholdSummary.criticalAlerts > 0 ? '#dc2626' : '#2563eb'
              }}
              onClick={() => onNavigate && onNavigate('performance')}
            >
              <span>Go to Safety Performance (SPI)</span>
              <ArrowRight size={14} />
            </button>
          </div>
        </div>
      )}

      {/* 3. Render Metric Cards Row */}
      {metricItems.length > 0 && (
        <div className="dash-metrics-grid">
          {metricItems.map((item, index) => renderMetricCard(item, index))}
        </div>
      )}

      {/* 4. Render Major Content Cards Grid (Tables & Feeds) */}
      {contentItems.length > 0 && (
        <div className="dash-tables-grid">
          {contentItems.map((item, index) => renderContentCard(item, index))}
        </div>
      )}

      {/* Add Widget Catalog Modal with Search & Category Filtering */}
      {showAddWidgetModal && (
        <div
          className="cal-modal-backdrop"
          onClick={() => setShowAddWidgetModal(false)}
        >
          <div
            className="cal-modal-container"
            style={{ maxWidth: '720px', width: '100%', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="cal-modal-header">
              <div className="cal-modal-title-group">
                <div className="cal-modal-icon-wrap">
                  <Grid size={20} color="#0f172a" />
                </div>
                <div>
                  <h3 className="cal-modal-title">Authorized Widget Catalog</h3>
                  <p className="cal-modal-subtitle">
                    Available for <b>{userRole}</b> based on module security boundaries
                  </p>
                </div>
              </div>
              <button
                className="cal-modal-close-btn"
                onClick={() => setShowAddWidgetModal(false)}
              >
                <X size={18} />
              </button>
            </div>

            {/* Search Bar & Category Filter Bar */}
            <div style={{ padding: '16px 24px 12px 24px', borderBottom: '1px solid #e2e8f0', backgroundColor: '#f8fafc' }}>
              {/* Search input with icon and clear button */}
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <Search size={16} color="#64748b" style={{ position: 'absolute', left: '12px', pointerEvents: 'none' }} />
                <input
                  type="text"
                  value={widgetSearchQuery}
                  onChange={(e) => setWidgetSearchQuery(e.target.value)}
                  placeholder="Search widgets by title, description, code, or module..."
                  style={{
                    width: '100%',
                    padding: '9px 36px 9px 36px',
                    borderRadius: '8px',
                    border: '1px solid #cbd5e1',
                    fontSize: '13px',
                    outline: 'none',
                    backgroundColor: '#ffffff',
                    color: '#0f172a',
                    boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.03)'
                  }}
                />
                {widgetSearchQuery && (
                  <button
                    type="button"
                    onClick={() => setWidgetSearchQuery('')}
                    style={{
                      position: 'absolute',
                      right: '10px',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '4px',
                      color: '#94a3b8',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                    title="Clear search"
                  >
                    <X size={15} />
                  </button>
                )}
              </div>

              {/* Filter pills & Count summary */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '12px', flexWrap: 'wrap', gap: '8px' }}>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={() => setWidgetCategoryFilter('ALL')}
                    style={{
                      padding: '4px 12px',
                      borderRadius: '20px',
                      fontSize: '12px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      border: '1px solid',
                      borderColor: widgetCategoryFilter === 'ALL' ? '#0f172a' : '#cbd5e1',
                      backgroundColor: widgetCategoryFilter === 'ALL' ? '#0f172a' : '#ffffff',
                      color: widgetCategoryFilter === 'ALL' ? '#ffffff' : '#475569',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    All ({availableToAdd.length})
                  </button>
                  <button
                    type="button"
                    onClick={() => setWidgetCategoryFilter('METRIC')}
                    style={{
                      padding: '4px 12px',
                      borderRadius: '20px',
                      fontSize: '12px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      border: '1px solid',
                      borderColor: widgetCategoryFilter === 'METRIC' ? '#2563eb' : '#cbd5e1',
                      backgroundColor: widgetCategoryFilter === 'METRIC' ? '#eff6ff' : '#ffffff',
                      color: widgetCategoryFilter === 'METRIC' ? '#1d4ed8' : '#475569',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    Metric Cards ({availableToAdd.filter((w) => w.category === 'METRIC').length})
                  </button>
                  <button
                    type="button"
                    onClick={() => setWidgetCategoryFilter('TABLE')}
                    style={{
                      padding: '4px 12px',
                      borderRadius: '20px',
                      fontSize: '12px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      border: '1px solid',
                      borderColor: widgetCategoryFilter === 'TABLE' ? '#0891b2' : '#cbd5e1',
                      backgroundColor: widgetCategoryFilter === 'TABLE' ? '#ecfeff' : '#ffffff',
                      color: widgetCategoryFilter === 'TABLE' ? '#0e7490' : '#475569',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    Tables ({availableToAdd.filter((w) => w.category === 'TABLE').length})
                  </button>
                  <button
                    type="button"
                    onClick={() => setWidgetCategoryFilter('FEED')}
                    style={{
                      padding: '4px 12px',
                      borderRadius: '20px',
                      fontSize: '12px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      border: '1px solid',
                      borderColor: widgetCategoryFilter === 'FEED' ? '#7c3aed' : '#cbd5e1',
                      backgroundColor: widgetCategoryFilter === 'FEED' ? '#f5f3ff' : '#ffffff',
                      color: widgetCategoryFilter === 'FEED' ? '#6d28d9' : '#475569',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    Activity Feeds ({availableToAdd.filter((w) => w.category === 'FEED').length})
                  </button>
                </div>

                <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 500 }}>
                  Showing <b>{filteredAvailableWidgets.length}</b> of <b>{availableToAdd.length}</b> available
                </span>
              </div>
            </div>

            {/* Scrollable Widget Items List */}
            <div style={{ padding: '20px 24px', flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {availableToAdd.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 20px', color: '#64748b' }}>
                  <CheckCircle2 size={36} color="#16a34a" style={{ margin: '0 auto 12px auto' }} />
                  <p style={{ fontWeight: 700, fontSize: '15px', color: '#0f172a', margin: 0 }}>
                    All authorized widgets are already active on your dashboard!
                  </p>
                  <p style={{ fontSize: '12px', color: '#64748b', margin: '4px 0 0 0' }}>
                    You have added all cards permitted under your role permissions.
                  </p>
                </div>
              ) : filteredAvailableWidgets.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 20px', color: '#64748b' }}>
                  <Search size={32} color="#94a3b8" style={{ margin: '0 auto 10px auto' }} />
                  <p style={{ fontWeight: 600, fontSize: '14px', color: '#0f172a', margin: 0 }}>
                    No authorized widgets found matching your criteria.
                  </p>
                  <p style={{ fontSize: '12px', color: '#64748b', margin: '4px 0 16px 0' }}>
                    Try searching for different keywords or clear the category filter.
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setWidgetSearchQuery('');
                      setWidgetCategoryFilter('ALL');
                    }}
                    style={{
                      padding: '6px 14px',
                      borderRadius: '6px',
                      backgroundColor: '#f1f5f9',
                      border: '1px solid #cbd5e1',
                      fontSize: '12px',
                      fontWeight: 600,
                      color: '#0f172a',
                      cursor: 'pointer'
                    }}
                  >
                    Clear Search & Filter
                  </button>
                </div>
              ) : (
                filteredAvailableWidgets.map((w) => {
                  const isMetric = w.category === 'METRIC';
                  const isTable = w.category === 'TABLE';
                  const catBg = isMetric ? '#eff6ff' : isTable ? '#ecfeff' : '#f5f3ff';
                  const catColor = isMetric ? '#1d4ed8' : isTable ? '#0e7490' : '#6d28d9';
                  const catBorder = isMetric ? '#bfdbfe' : isTable ? '#a5f3fc' : '#ddd6fe';

                  return (
                    <div
                      key={w.id}
                      style={{
                        border: '1px solid #e2e8f0',
                        borderRadius: '12px',
                        padding: '14px 16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        backgroundColor: '#ffffff',
                        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.02)',
                        transition: 'border-color 0.15s ease, box-shadow 0.15s ease'
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0, paddingRight: '16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                          <span style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a' }}>
                            {w.title}
                          </span>
                          <span
                            style={{
                              fontSize: '10px',
                              fontWeight: 700,
                              padding: '2px 7px',
                              borderRadius: '4px',
                              backgroundColor: catBg,
                              color: catColor,
                              border: `1px solid ${catBorder}`,
                              textTransform: 'uppercase',
                              letterSpacing: '0.04em'
                            }}
                          >
                            {w.category}
                          </span>
                          <span
                            style={{
                              fontSize: '10px',
                              fontWeight: 600,
                              color: '#64748b',
                              fontFamily: 'monospace'
                            }}
                          >
                            {w.code}
                          </span>
                        </div>
                        <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px', lineHeight: 1.4 }}>
                          {w.description} • Module: <b style={{ color: '#334155' }}>{w.requiredModule}</b>
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() => handleAddWidgetToStaged(w)}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '5px',
                          padding: '7px 16px',
                          borderRadius: '6px',
                          backgroundColor: '#0f172a',
                          color: '#ffffff',
                          border: 'none',
                          fontSize: '12px',
                          fontWeight: 600,
                          cursor: 'pointer',
                          flexShrink: 0,
                          transition: 'background-color 0.15s ease'
                        }}
                      >
                        <Plus size={14} />
                        <span>Add</span>
                      </button>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

      {/* View All Activity Logs Modal Dialog */}
      {viewAllModalType && (
        <div
          className="cal-modal-backdrop"
          onClick={() => {
            setViewAllModalType(null);
            setViewAllSearch('');
            setSystemFilterDomain('ALL');
          }}
        >
          <div
            className="cal-modal-container"
            style={{ maxWidth: '780px', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="cal-modal-header">
              <div className="cal-modal-title-group">
                <div
                  className="cal-modal-icon-wrap"
                  style={{
                    backgroundColor: viewAllModalType === 'report' ? '#eff6ff' : '#f8fafc'
                  }}
                >
                  {viewAllModalType === 'report' ? (
                    <MessageSquare size={20} color="#2563eb" />
                  ) : (
                    <Settings size={20} color="#475569" />
                  )}
                </div>
                <div>
                  <h3 className="cal-modal-title">
                    {viewAllModalType === 'report'
                      ? 'Report Activity Feed — Complete Log History'
                      : 'System Activity Feed — Authorized Domain Audit Trail'}
                  </h3>
                  <p className="cal-modal-subtitle">
                    {viewAllModalType === 'report'
                      ? 'All additions, attachments, reviews and status updates directly involving your assigned reports'
                      : `Live updates across modules authorized for role: ${userRole} (MoC, Audits, Compliance, Config, Integrations)`}
                  </p>
                </div>
              </div>
              <button
                type="button"
                className="cal-modal-close-btn"
                onClick={() => {
                  setViewAllModalType(null);
                  setViewAllSearch('');
                  setSystemFilterDomain('ALL');
                }}
              >
                <X size={18} />
              </button>
            </div>

            {/* Modal Filter Toolbar */}
            <div
              style={{
                padding: '14px 24px',
                borderBottom: '1px solid #e2e8f0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '12px',
                flexWrap: 'wrap',
                backgroundColor: '#f8fafc'
              }}
            >
              <div style={{ position: 'relative', flex: 1, minWidth: '220px' }}>
                <Search
                  size={14}
                  style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }}
                />
                <input
                  type="text"
                  placeholder="Filter logs by keyword, code, actor or report..."
                  value={viewAllSearch}
                  onChange={(e) => setViewAllSearch(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '6px 10px 6px 30px',
                    fontSize: '12.5px',
                    border: '1px solid #cbd5e1',
                    borderRadius: '6px',
                    outline: 'none',
                    backgroundColor: '#ffffff'
                  }}
                />
              </div>

              {viewAllModalType === 'system' && (
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                  <span style={{ fontSize: '11.5px', fontWeight: 600, color: '#64748b' }}>Domain:</span>
                  {['ALL', 'moc', 'audits', 'regulations', 'documents'].map((dom) => (
                    <button
                      key={dom}
                      type="button"
                      onClick={() => setSystemFilterDomain(dom)}
                      style={{
                        padding: '4px 9px',
                        borderRadius: '999px',
                        fontSize: '11px',
                        fontWeight: 600,
                        border: '1px solid',
                        borderColor: systemFilterDomain === dom ? '#2563eb' : '#cbd5e1',
                        backgroundColor: systemFilterDomain === dom ? '#2563eb' : '#ffffff',
                        color: systemFilterDomain === dom ? '#ffffff' : '#475569',
                        cursor: 'pointer'
                      }}
                    >
                      {dom === 'ALL' ? 'All' : dom.toUpperCase()}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Modal Body List */}
            <div style={{ padding: '20px 24px', overflowY: 'auto', flex: 1 }}>
              {viewAllModalType === 'report' ? (
                <div className="dash-feed-timeline-list">
                  {visibleReportActivityFeed
                    .filter((log) => {
                      if (!viewAllSearch.trim()) return true;
                      const q = viewAllSearch.toLowerCase();
                      return (
                        log.actorName.toLowerCase().includes(q) ||
                        log.actionText.toLowerCase().includes(q) ||
                        log.tag.toLowerCase().includes(q)
                      );
                    })
                    .map((log, index, arr) => (
                      <div key={log.id} className="dash-feed-item-row">
                        <div className="dash-feed-indicator">
                          <div
                            className="dash-feed-avatar"
                            style={{ backgroundColor: log.avatarBg }}
                          >
                            {log.actorInitials}
                          </div>
                          {index < arr.length - 1 && (
                            <div className="dash-feed-timeline-line" />
                          )}
                        </div>

                        <div className="dash-feed-body">
                          <div className="dash-feed-header-line">
                            <span className="dash-feed-actor">
                              {log.actorName}{' '}
                              <span className="dash-feed-dept">({log.actorDepartment})</span>
                            </span>
                            <span className="dash-feed-timestamp">{log.timestamp}</span>
                          </div>

                          <p className="dash-feed-action-text">{log.actionText}</p>

                          <button
                            type="button"
                            className="dash-feed-tag-pill"
                            onClick={() => {
                              setViewAllModalType(null);
                              onNavigate && onNavigate(log.targetTab, { id: log.targetId });
                            }}
                            title={`Open ${log.tag}`}
                          >
                            <span>{log.tag}</span>
                          </button>
                        </div>
                      </div>
                    ))}
                </div>
              ) : (
                <div className="dash-feed-timeline-list">
                  {visibleSystemActivityFeed
                    .filter((log) => {
                      if (systemFilterDomain !== 'ALL' && log.domainModule !== systemFilterDomain) {
                        return false;
                      }
                      if (!viewAllSearch.trim()) return true;
                      const q = viewAllSearch.toLowerCase();
                      return (
                        log.domainTitle.toLowerCase().includes(q) ||
                        log.actionText.toLowerCase().includes(q) ||
                        log.tag.toLowerCase().includes(q)
                      );
                    })
                    .map((log, index, arr) => (
                      <div key={log.id} className="dash-feed-item-row">
                        <div className="dash-feed-indicator">
                          <div
                            className="dash-feed-icon-node"
                            style={{ backgroundColor: log.iconColor }}
                          >
                            {renderSystemIcon(log.iconType)}
                          </div>
                          {index < arr.length - 1 && (
                            <div className="dash-feed-timeline-line" />
                          )}
                        </div>

                        <div className="dash-feed-body">
                          <div className="dash-feed-header-line">
                            <span className="dash-feed-actor">{log.domainTitle}</span>
                            <span className="dash-feed-timestamp">{log.timestamp}</span>
                          </div>

                          <p className="dash-feed-action-text">{log.actionText}</p>

                          <button
                            type="button"
                            className="dash-feed-tag-pill"
                            onClick={() => {
                              setViewAllModalType(null);
                              onNavigate && onNavigate(log.targetTab, { id: log.targetId });
                            }}
                            title={`Open ${log.tag}`}
                          >
                            <span>{log.tag}</span>
                          </button>
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* KPI Card Indicator Breakdown Pop-up Modal */}
      {indicatorModal && (
        <div
          className="cal-modal-backdrop"
          onClick={() => {
            setIndicatorModal(null);
            setIndicatorSearch('');
          }}
        >
          <div
            className="cal-modal-container"
            style={{ maxWidth: '920px', maxHeight: '88vh', display: 'flex', flexDirection: 'column' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="cal-modal-header">
              <div className="cal-modal-title-group">
                <div
                  className="cal-modal-icon-wrap"
                  style={{
                    backgroundColor:
                      indicatorModal.dotColorClass === 'dot-red'
                        ? '#fef2f2'
                        : indicatorModal.dotColorClass === 'dot-amber'
                        ? '#fffbeb'
                        : indicatorModal.dotColorClass === 'dot-blue'
                        ? '#eff6ff'
                        : indicatorModal.dotColorClass === 'dot-green'
                        ? '#ecfdf5'
                        : indicatorModal.dotColorClass === 'dot-purple'
                        ? '#f5f3ff'
                        : '#f8fafc'
                  }}
                >
                  <span
                    className={`dash-ind-dot ${indicatorModal.dotColorClass}`}
                    style={{ width: '12px', height: '12px' }}
                  />
                </div>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <h3 className="cal-modal-title" style={{ margin: 0 }}>
                      {indicatorModal.cardTitle}
                    </h3>
                    <span
                      className={`dash-ind-badge ${
                        indicatorModal.dotColorClass === 'dot-red'
                          ? 'dash-ind-badge-red'
                          : indicatorModal.dotColorClass === 'dot-amber'
                          ? 'dash-ind-badge-amber'
                          : indicatorModal.dotColorClass === 'dot-blue'
                          ? 'dash-ind-badge-blue'
                          : indicatorModal.dotColorClass === 'dot-green'
                          ? 'dash-ind-badge-green'
                          : indicatorModal.dotColorClass === 'dot-purple'
                          ? 'dash-ind-badge-purple'
                          : 'dash-ind-badge-gray'
                      }`}
                    >
                      {indicatorModal.filterLabel}
                    </span>
                  </div>
                  <p className="cal-modal-subtitle">{indicatorModal.subtitle}</p>
                </div>
              </div>
              <button
                type="button"
                className="cal-modal-close-btn"
                onClick={() => {
                  setIndicatorModal(null);
                  setIndicatorSearch('');
                }}
                title="Close"
              >
                <X size={18} />
              </button>
            </div>

            {/* Modal Filter Toolbar */}
            <div
              style={{
                padding: '12px 24px',
                borderBottom: '1px solid #e2e8f0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '12px',
                backgroundColor: '#f8fafc'
              }}
            >
              <div style={{ position: 'relative', flex: 1, minWidth: '240px' }}>
                <Search
                  size={14}
                  style={{
                    position: 'absolute',
                    left: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#94a3b8'
                  }}
                />
                <input
                  type="text"
                  placeholder="Filter records by ID, title, category, status or assignee..."
                  value={indicatorSearch}
                  onChange={(e) => setIndicatorSearch(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '6px 10px 6px 30px',
                    fontSize: '12.5px',
                    border: '1px solid #cbd5e1',
                    borderRadius: '6px',
                    outline: 'none',
                    backgroundColor: '#ffffff'
                  }}
                />
                {indicatorSearch && (
                  <button
                    type="button"
                    onClick={() => setIndicatorSearch('')}
                    style={{
                      position: 'absolute',
                      right: '8px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'transparent',
                      border: 'none',
                      color: '#94a3b8',
                      cursor: 'pointer',
                      fontSize: '11px'
                    }}
                  >
                    Clear
                  </button>
                )}
              </div>
              <div style={{ fontSize: '12px', color: '#64748b', fontWeight: 600 }}>
                {
                  indicatorModal.records.filter((r) => {
                    if (!indicatorSearch.trim()) return true;
                    const q = indicatorSearch.toLowerCase();
                    return (
                      r.id.toLowerCase().includes(q) ||
                      r.title.toLowerCase().includes(q) ||
                      r.category.toLowerCase().includes(q) ||
                      r.status.toLowerCase().includes(q) ||
                      (r.assignedTo && r.assignedTo.toLowerCase().includes(q)) ||
                      (r.summary && r.summary.toLowerCase().includes(q))
                    );
                  }).length
                }{' '}
                of {indicatorModal.records.length} records
              </div>
            </div>

            {/* Table Body */}
            <div style={{ padding: '0', overflowY: 'auto', flex: 1 }}>
              <div className="dash-ind-modal-table-wrap">
                <table className="dash-ind-modal-table">
                  <thead>
                    <tr>
                      <th style={{ width: '130px' }}>Reference</th>
                      <th>Title & Description</th>
                      <th style={{ width: '140px' }}>Category</th>
                      <th style={{ width: '150px' }}>Status / Severity</th>
                      <th style={{ width: '140px' }}>Timeline / Due</th>
                      <th style={{ width: '100px', textAlign: 'right' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {indicatorModal.records
                      .filter((r) => {
                        if (!indicatorSearch.trim()) return true;
                        const q = indicatorSearch.toLowerCase();
                        return (
                          r.id.toLowerCase().includes(q) ||
                          r.title.toLowerCase().includes(q) ||
                          r.category.toLowerCase().includes(q) ||
                          r.status.toLowerCase().includes(q) ||
                          (r.assignedTo && r.assignedTo.toLowerCase().includes(q)) ||
                          (r.summary && r.summary.toLowerCase().includes(q))
                        );
                      })
                      .map((rec) => (
                        <tr key={rec.id}>
                          <td>
                            <span className="dash-ind-ref-badge">{rec.id}</span>
                          </td>
                          <td>
                            <div style={{ fontWeight: 600, color: '#0f172a', marginBottom: '2px' }}>
                              {rec.title}
                            </div>
                            {rec.summary && (
                              <div style={{ fontSize: '11.5px', color: '#64748b', lineHeight: 1.4 }}>
                                {rec.summary}
                              </div>
                            )}
                            {rec.assignedTo && (
                              <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>
                                Owner: {rec.assignedTo}
                              </div>
                            )}
                          </td>
                          <td>
                            <span style={{ fontSize: '12px', color: '#475569', fontWeight: 500 }}>
                              {rec.category}
                            </span>
                          </td>
                          <td>
                            <span className={`dash-ind-badge dash-ind-badge-${rec.statusBadgeVariant}`}>
                              {rec.status}
                            </span>
                          </td>
                          <td>
                            <span style={{ fontSize: '11.5px', color: '#64748b', fontWeight: 500 }}>
                              {rec.timeInfo}
                            </span>
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            <button
                              type="button"
                              className="dash-ind-action-btn"
                              onClick={() => {
                                const targetTab = rec.targetTab;
                                const targetId = rec.targetId || rec.id;
                                setIndicatorModal(null);
                                setIndicatorSearch('');
                                if ((targetTab === 'ingestion' || indicatorModal.targetTab === 'ingestion') && targetId) {
                                  setSelectedReportIdForModal(targetId);
                                } else {
                                  onNavigate && onNavigate(targetTab, { id: targetId });
                                }
                              }}
                              title={`Open ${rec.id}`}
                            >
                              <span>Open</span>
                              <ChevronRight size={13} />
                            </button>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Modal Footer */}
            <div
              style={{
                padding: '14px 24px',
                borderTop: '1px solid #e2e8f0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                backgroundColor: '#f8fafc'
              }}
            >
              <button
                type="button"
                className="dash-btn dash-btn-secondary"
                onClick={() => {
                  setIndicatorModal(null);
                  setIndicatorSearch('');
                }}
              >
                Close
              </button>
              <button
                type="button"
                className="dash-btn dash-btn-primary"
                onClick={() => {
                  const target = indicatorModal.targetTab;
                  setIndicatorModal(null);
                  setIndicatorSearch('');
                  onNavigate && onNavigate(target);
                }}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
              >
                <span>Go to {indicatorModal.cardTitle} Module</span>
                <ExternalLink size={13} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Direct Report Assessment Modal */}
      <ReportDetailsModal
        isOpen={!!selectedReportIdForModal || !!selectedReportIdForEdit}
        reportId={selectedReportIdForModal || selectedReportIdForEdit}
        initialEditMode={!!selectedReportIdForEdit}
        onClose={() => {
          setSelectedReportIdForModal(null);
          setSelectedReportIdForEdit(null);
        }}
        onReportUpdated={() => {
          loadDashboardData();
        }}
        onNavigateToIngestion={(id) => {
          setSelectedReportIdForModal(null);
          setSelectedReportIdForEdit(null);
          if (onNavigate) {
            onNavigate('ingestion', id ? { reportId: id } : undefined);
          }
        }}
      />
    </div>
  );
};
